import React, { useEffect, useState } from 'react';
import {
  acquireWinProductImagesClient,
  analyzeCompetitorChannelClient,
  analyzeCompetitorVideoClient,
  deleteAffiliateChannelClient,
  downloadCompetitorVideoClient,
  fetchAffiliateChannels,
  fetchCompetitorVideos,
  fetchFoodSkillsAudit,
  fetchOwnGeneratedVideos,
  fetchResearchMappingTable,
  fetchWinProductsInventory,
  resetDefaultAffiliateChannelsClient,
  runAutomatedResearchPipelineClient,
  reviewOwnGeneratedVideoClient,
  saveAffiliateChannel,
  updateFoodSkillsClient,
  type AffiliateChannelProfile,
  type AutomatedResearchPipelineReport,
  type CompetitorAccountProfile,
  type CompetitorChannelEvaluation,
  type CompetitorVideoEvaluation,
  type CompetitorVideoItem,
  type CompetitorVideoMappingEntry,
  type MmoNicheId,
  type OwnGeneratedVideoItem,
  type OwnGeneratedVideoReview,
  type UgcFoodSkillsAuditReport,
  type WinProductImageResult,
  type WinProductImageSource,
  type WinProductInventoryItem,
} from '../services/affiliateHarnessClient';

const NICHE_OPTIONS: Array<{ id: MmoNicheId; label: string; group: 'food' | 'extended' }> = [
  { id: 'sauce', label: 'Mắm & Sốt gia vị', group: 'food' },
  { id: 'snacks', label: 'Đồ ăn vặt', group: 'food' },
  { id: 'candy', label: 'Bánh kẹo & Trà sữa', group: 'food' },
  { id: 'dried', label: 'Đồ khô & Đặc sản', group: 'food' },
  { id: 'farm', label: 'Nông sản & Hạt', group: 'food' },
  { id: 'kitchen', label: 'Đồ bếp', group: 'extended' },
  { id: 'health', label: 'Sức khỏe', group: 'extended' },
  { id: 'beauty', label: 'Mỹ phẩm', group: 'extended' },
  { id: 'skincare', label: 'Chăm sóc da', group: 'extended' },
  { id: 'women', label: 'Thời trang nữ', group: 'extended' },
  { id: 'men', label: 'Thời trang nam', group: 'extended' },
  { id: 'home', label: 'Gia dụng', group: 'extended' },
  { id: 'baby', label: 'Mẹ và bé', group: 'extended' },
  { id: 'phone', label: 'Phụ kiện ĐT', group: 'extended' },
  { id: 'cases', label: 'Ốp lưng ĐT', group: 'extended' },
  { id: 'pets', label: 'Thú cưng', group: 'extended' },
  { id: 'hair', label: 'Chăm sóc tóc', group: 'extended' },
  { id: 'shoes', label: 'Giày dép', group: 'extended' },
  { id: 'bags', label: 'Túi ví', group: 'extended' },
  { id: 'sports', label: 'Thể thao', group: 'extended' },
];

const getCompNiches = (comp: { niche?: MmoNicheId; niches?: MmoNicheId[] }): MmoNicheId[] =>
  comp.niches && comp.niches.length > 0 ? comp.niches : [comp.niche ?? 'sauce'];

const PRESET_COMPETITOR_POOL: CompetitorAccountProfile[] = [
  {
    id: 'comp-taphoabeoo235',
    handle: '@taphoabeoo235',
    displayName: 'Tạp Hóa Béo 235 · 500 Video Viral',
    niche: 'snacks',
    niches: ['snacks', 'candy', 'sauce', 'dried', 'farm'],
    followerEstimate: '320K+',
    avgViewsEstimate: '80K - 450K / video',
    hookFormula:
      'Mẹt tre lót lá chuối ngoài hiên gỗ nắng vàng + POV tay thật múc/rưới sốt hoặc chiên bánh xèo xèo ngay 1.5s đầu',
    strengths: [
      'Ánh sáng tự nhiên nắng vàng xuyên tán lá / hồ sen cực cuốn mắt',
      'Bố cục bàn ăn đầy đặn (Abundant Table) với mẹt tre đan + đĩa sứ trắng + chén thủy tinh',
      'Hộp/hũ sản phẩm thật đặt rõ nét phía sau món ăn đã chế biến',
    ],
    weaknesses: ['Một số video lặp lại cùng góc máy 45 độ'],
    videoFiles: [
      'taphoabeoo235_muoitieusolachanh_7695987499268705588.mp4',
      'taphoabeoo235_xotphomaitanozy_7696325760042142997.mp4',
      'taphoabeoo235_banhngochien_7696035035694075156.mp4',
      'taphoabeoo235_banhmihealthy_7696033422824721665.mp4',
      'taphoabeoo235_dauphongcotdua_7695985914916457748.mp4',
      'taphoabeoo235_medacdua_7695984667169721621.mp4',
    ],
  },
  {
    id: 'comp-taphoacoc',
    handle: '@taphoacoc',
    displayName: 'Tạp Hóa Cóc · Ăn Vặt, Sốt & Đặc Sản',
    niche: 'candy',
    niches: ['candy', 'sauce', 'snacks', 'dried', 'farm'],
    followerEstimate: '210K+',
    avgViewsEstimate: '50K - 280K / video',
    hookFormula:
      'Cận cảnh chén chè / niêu tôm thịt kho tiêu sóng sánh ánh nắng + mở hộp ăn liền tiện lợi trong 2 giây đầu',
    strengths: [
      'Màu sắc topping (hạt lựu đỏ, bánh lọt xanh, nước cốt dừa trắng, sốt kho tiêu nâu đỏ) tương phản cực cao',
      'Nhịp cắt nhanh, tập trung vào độ sánh và độ giòn sần sật',
    ],
    weaknesses: ['Bối cảnh nền đôi khi hơi hẹp'],
    videoFiles: [
      'taphoacoc_suongsahatluu_7695990004937395476.mp4',
      'taphoacoc_tomthitkhotieu_7695988801721748757.mp4',
      'taphoacoc-7690930753588284693-20261005-hd.mp4',
    ],
  },
  {
    id: 'comp-lang-chai',
    handle: '@langchaixua_official',
    displayName: 'Làng Chài Xưa Official',
    niche: 'sauce',
    niches: ['sauce', 'dried', 'kitchen'],
    followerEstimate: '185K',
    avgViewsEstimate: '45K - 120K / video',
    hookFormula: 'Rưới sốt muối ớt xanh sệt óng lên hải sản tươi trong 2 giây đầu',
    strengths: ['Cận cảnh độ sệt của sốt cực nét', 'Âm thanh chấm hải sản ASMR kích thích vị giác'],
    weaknesses: ['Ít đổi góc máy ở nửa sau video'],
    videoFiles: ['sotmuoichamlangchai.mp4', 'video_1776138951557.mp4'],
  },
  {
    id: 'comp-mam-tep-mien-tay',
    handle: '@mamtep_mientay',
    displayName: 'Đặc Sản Mắm Tép Miền Tây',
    niche: 'sauce',
    niches: ['sauce', 'dried'],
    followerEstimate: '92K',
    avgViewsEstimate: '28K - 85K / video',
    hookFormula: 'Mở nắp hũ mắm tép đỏ au + ăn kèm thịt luộc cuốn rau sống',
    strengths: ['Màu sắc thực phẩm ấm, tạo cảm giác nhà làm uy tín'],
    weaknesses: ['Nhịp cắt hơi chậm ở giây 6-10'],
    videoFiles: [],
  },
  {
    id: 'comp-an-vat-88',
    handle: '@anvat_noidiatrung88',
    displayName: 'Trùm Ăn Vặt Nội Địa & Chân Gà',
    niche: 'snacks',
    niches: ['snacks', 'candy'],
    followerEstimate: '240K',
    avgViewsEstimate: '60K - 210K / video',
    hookFormula: 'Bóc gói chân gà / trà sữa phát tiếng sột soạt + cắn giòn sần sật ngay giây 1',
    strengths: ['Hook âm thanh ASMR rất mạnh', 'Bắt trend từ khóa GenZ nhanh'],
    weaknesses: ['Bối cảnh đôi khi lộn xộn'],
    videoFiles: ['trasuavanchan.mp4', 'video_1776140119046.mp4'],
  },
  {
    id: 'comp-kho-bo-tay-bac',
    handle: '@kho_bo_tay_bac_chuan',
    displayName: 'Khô Bò & Đặc Sản Tây Bắc',
    niche: 'dried',
    niches: ['dried', 'snacks', 'sauce'],
    followerEstimate: '130K',
    avgViewsEstimate: '35K - 95K / video',
    hookFormula: 'Xé thớ thịt khô bò sợi dài đỏ nâu + vắt tắc sủi bọt cận cảnh',
    strengths: ['Thể hiện rõ thớ thịt thật, cam kết hoàn tiền mạnh'],
    weaknesses: ['Giọng đọc đôi khi hơi đều'],
    videoFiles: [],
  },
  {
    id: 'comp-nong-san-xanh',
    handle: '@nongsan_hatdinhduong_vn',
    displayName: 'Nông Sản & Hạt Dinh Dưỡng Xanh',
    niche: 'farm',
    niches: ['farm', 'dried', 'health'],
    followerEstimate: '110K',
    avgViewsEstimate: '30K - 80K / video',
    hookFormula: 'Bẻ đôi hạt điều / xoài sấy dẻo vàng ươm ngay trước ống kính',
    strengths: ['Ánh sáng tự nhiên trong trẻo, bao bì zip rõ nét'],
    weaknesses: ['CTA cuối video chưa tạo cảm giác khan hiếm'],
    videoFiles: [],
  },
];

type HubSubTab =
  | 'competitors'
  | 'own_review'
  | 'win_images'
  | 'food_skills'
  | 'classic_youwee';

const STAGE_BADGE_MAP: Record<string, { label: string; color: string; bg: string }> = {
  packaging_raw: { label: 'Bao Bì & Nguyên Liệu Gốc', color: '#f59e0b', bg: 'rgba(245,158,11,0.2)' },
  cooking_in_progress: { label: 'Đang Nấu / Chế Biến (DIY)', color: '#fb923c', bg: 'rgba(251,146,60,0.22)' },
  cooked_final_dish: { label: 'Món Hoàn Thành Sau Khi Nấu (DIY)', color: '#fbbf24', bg: 'rgba(251,191,36,0.22)' },
  texture_closeup: { label: 'Cận Cảnh Chất Liệu & Chi Tiết', color: '#e879f9', bg: 'rgba(232,121,249,0.2)' },
  money_shot: { label: 'Money-Shot Thực Tế Chuẩn AI', color: '#f87171', bg: 'rgba(248,113,113,0.22)' },
};

export const YouweeResearchHubTab: React.FC = () => {
  const [subTab, setSubTab] = useState<HubSubTab>('competitors');
  const [channels, setChannels] = useState<AffiliateChannelProfile[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<string>('ch-dundimmean-main');
  const [filterByChannel, setFilterByChannel] = useState<boolean>(false);
  const [channelStatusMsg, setChannelStatusMsg] = useState<string | null>(null);

  // Multi-Niche & Multi-Competitor Channel Form State (supports both Create & Edit)
  const [showChannelForm, setShowChannelForm] = useState(false);
  const [editingChannelId, setEditingChannelId] = useState<string | null>(null);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelHandle, setNewChannelHandle] = useState('');
  const [newChannelNiches, setNewChannelNiches] = useState<MmoNicheId[]>(['snacks', 'candy', 'sauce', 'dried', 'farm']);
  const [showAll20Niches, setShowAll20Niches] = useState(false);
  const [newChannelKeywords, setNewChannelKeywords] = useState('');
  const [draftCompetitors, setDraftCompetitors] = useState<CompetitorAccountProfile[]>([
    PRESET_COMPETITOR_POOL[0]!,
    PRESET_COMPETITOR_POOL[1]!,
  ]);
  const [newCompHandle, setNewCompHandle] = useState('');
  const [newCompName, setNewCompName] = useState('');
  const [newCompNiches, setNewCompNiches] = useState<MmoNicheId[]>(['sauce', 'snacks']);
  const [newCompHook, setNewCompHook] = useState('');

  // Multi-filter for competitor evaluation on active channel
  const [selectedEvalNiches, setSelectedEvalNiches] = useState<MmoNicheId[]>([]);
  const [selectedEvalCompetitors, setSelectedEvalCompetitors] = useState<string[]>([]);

  // Automated Pipeline (Steps 1, 2, 3, 4 & All) + Mapping Table state
  const [mappingTable, setMappingTable] = useState<CompetitorVideoMappingEntry[]>([]);
  const [autoRunningStep, setAutoRunningStep] = useState<string | null>(null);
  const [autoReport, setAutoReport] = useState<AutomatedResearchPipelineReport | null>(null);

  // Competitor videos & analysis state
  const [competitorVideos, setCompetitorVideos] = useState<CompetitorVideoItem[]>([]);
  const [selectedCompetitorVideo, setSelectedCompetitorVideo] = useState<CompetitorVideoItem | null>(null);
  const [channelEval, setChannelEval] = useState<CompetitorChannelEvaluation | null>(null);
  const [videoEval, setVideoEval] = useState<CompetitorVideoEvaluation | null>(null);
  const [loadingCompAnalysis, setLoadingCompAnalysis] = useState(false);
  const [downloadVideoUrl, setDownloadVideoUrl] = useState('');
  const [downloadFilenameHint, setDownloadFilenameHint] = useState('');
  const [downloadingVideo, setDownloadingVideo] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState<string | null>(null);

  // Own generated videos & review state
  const [ownVideos, setOwnVideos] = useState<OwnGeneratedVideoItem[]>([]);
  const [selectedOwnVideo, setSelectedOwnVideo] = useState<OwnGeneratedVideoItem | null>(null);
  const [ownReview, setOwnReview] = useState<OwnGeneratedVideoReview | null>(null);
  const [loadingOwnReview, setLoadingOwnReview] = useState(false);

  // Step 3: Win Product >=6 Image Curation state
  const [winInventory, setWinInventory] = useState<WinProductInventoryItem[]>([]);
  const [selectedWinItem, setSelectedWinItem] = useState<WinProductInventoryItem | null>(null);
  const [winProductName, setWinProductName] = useState('Sốt Muối Chấm Hải Sản Làng Chài Xưa');
  const [winSource, setWinSource] = useState<WinProductImageSource>('auto');
  const [winCompetitorFile, setWinCompetitorFile] = useState('sotmuoichamlangchai.mp4');
  const [winSearchQuery, setWinSearchQuery] = useState('');
  const [acquiringImages, setAcquiringImages] = useState(false);
  const [lastAcquiredResult, setLastAcquiredResult] = useState<WinProductImageResult | null>(null);

  // Step 4: UGC_food_skills Audit & Updater state
  const [skillsAudit, setSkillsAudit] = useState<UgcFoodSkillsAuditReport | null>(null);
  const [skillTitle, setSkillTitle] = useState('');
  const [skillContent, setSkillContent] = useState('');
  const [skillDishSlug, setSkillDishSlug] = useState('condiments');
  const [updatingSkill, setUpdatingSkill] = useState(false);
  const [skillUpdateNotice, setSkillUpdateNotice] = useState<string | null>(null);

  const selectedChannel = channels.find((c) => c.id === selectedChannelId) ?? channels[0];

  const loadInitialData = async (channelId = selectedChannelId, onlyChannel = filterByChannel) => {
    try {
      const [chList, compData, ownData, winData, auditData, mapData] = await Promise.all([
        fetchAffiliateChannels(),
        fetchCompetitorVideos(onlyChannel ? channelId : 'all'),
        fetchOwnGeneratedVideos(onlyChannel ? channelId : 'all'),
        fetchWinProductsInventory(),
        fetchFoodSkillsAudit(),
        fetchResearchMappingTable(),
      ]);
      setChannels(chList);
      if (chList.length > 0 && !chList.some((c) => c.id === channelId)) {
        setSelectedChannelId(chList[0]!.id);
      }
      setCompetitorVideos(compData.videos);
      if (compData.videos.length > 0 && !selectedCompetitorVideo) {
        setSelectedCompetitorVideo(compData.videos[0]!);
        setWinCompetitorFile(compData.videos[0]!.filename);
      }
      setOwnVideos(ownData.videos);
      if (ownData.videos.length > 0 && !selectedOwnVideo) {
        setSelectedOwnVideo(ownData.videos[0]!);
      }
      setWinInventory(winData.items);
      if (winData.items.length > 0) {
        setSelectedWinItem((prev) =>
          prev ? winData.items.find((x) => x.folderName === prev.folderName) ?? winData.items[0]! : winData.items[0]!,
        );
      }
      setSkillsAudit(auditData);
      setMappingTable(Array.isArray(mapData) ? mapData : []);
    } catch (err) {
      console.error('Failed to load Youwee Research Hub data:', err);
    }
  };

  // Load initial data once on mount so channel list never jumps when switching/deleting channels
  useEffect(() => {
    void loadInitialData(selectedChannelId, filterByChannel);
  }, []);

  // Only refresh filtered videos when selectedChannelId or filterByChannel changes
  useEffect(() => {
    const refreshFilteredVideos = async () => {
      try {
        const [compData, ownData] = await Promise.all([
          fetchCompetitorVideos(filterByChannel ? selectedChannelId : 'all'),
          fetchOwnGeneratedVideos(filterByChannel ? selectedChannelId : 'all'),
        ]);
        setCompetitorVideos(compData.videos);
        setOwnVideos(ownData.videos);
      } catch (err) {
        console.error('Failed to refresh filtered videos:', err);
      }
    };
    void refreshFilteredVideos();
  }, [selectedChannelId, filterByChannel]);

  useEffect(() => {
    if (!selectedChannel) return;
    const chNiches = selectedChannel.niches?.length ? selectedChannel.niches : [selectedChannel.niche];
    const chComps = selectedChannel.competitors.map((c) => c.handle);
    setSelectedEvalNiches(chNiches);
    setSelectedEvalCompetitors(chComps);
    void handleAnalyzeCompetitorChannel(selectedChannel.id, chNiches, chComps);
  }, [selectedChannel?.id]);

  const handleAnalyzeCompetitorChannel = async (
    channelId = selectedChannelId,
    nichesOverride?: MmoNicheId[],
    compHandlesOverride?: string[],
  ) => {
    setLoadingCompAnalysis(true);
    try {
      const res = await analyzeCompetitorChannelClient({
        channelId,
        niches: nichesOverride ?? (selectedEvalNiches.length > 0 ? selectedEvalNiches : undefined),
        competitorHandles:
          compHandlesOverride ?? (selectedEvalCompetitors.length > 0 ? selectedEvalCompetitors : undefined),
      });
      setChannelEval(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCompAnalysis(false);
    }
  };

  // Run Automated Pipeline for Step 1, Step 2, Step 3, Step 4, or ALL Steps 1->4 from Win_Products
  const handleAutoRunPipeline = async (step: 'step1' | 'step2' | 'step3' | 'step4' | 'all') => {
    setAutoRunningStep(step);
    try {
      const res = await runAutomatedResearchPipelineClient({
        step,
        channelId: selectedChannelId,
        niches: selectedEvalNiches.length > 0 ? selectedEvalNiches : undefined,
        competitorHandles: selectedEvalCompetitors.length > 0 ? selectedEvalCompetitors : undefined,
      });
      setAutoReport(res);
      if (res.mappingTable) {
        setMappingTable(res.mappingTable);
      }
      if (res.step1Result) {
        if (res.step1Result.channelEvaluations[0]) {
          setChannelEval(res.step1Result.channelEvaluations[0]);
        }
        if (res.step1Result.videoEvaluations[0]) {
          setVideoEval(res.step1Result.videoEvaluations[0]);
        }
      }
      if (res.step2Result && res.step2Result.ownReviews[0]) {
        setOwnReview(res.step2Result.ownReviews[0]);
      }
      if (res.step4Result) {
        setSkillsAudit(res.step4Result.skillsAudit);
      }
      if (res.step3Result?.inventory) {
        setWinInventory(res.step3Result.inventory);
        if (res.step3Result.inventory.length > 0) {
          setSelectedWinItem(res.step3Result.inventory[0]!);
        }
      } else {
        const inv = await fetchWinProductsInventory();
        setWinInventory(inv.items);
      }
    } catch (err) {
      console.error('Failed to run automated research pipeline:', err);
    } finally {
      setAutoRunningStep(null);
    }
  };

  const handleAnalyzeCompetitorVideo = async (video: CompetitorVideoItem, extractWinImages = false) => {
    setSelectedCompetitorVideo(video);
    setLoadingCompAnalysis(true);
    try {
      const res = await analyzeCompetitorVideoClient({
        filename: video.filename,
        channelId: selectedChannelId,
        extractWinProductImages: extractWinImages,
        winProductName: video.inferredProductName,
      });
      setVideoEval(res);
      if (extractWinImages && res.extractedWinProductImages) {
        setLastAcquiredResult(res.extractedWinProductImages);
        const [inv, mapRes] = await Promise.all([fetchWinProductsInventory(), fetchResearchMappingTable()]);
        setWinInventory(inv.items);
        setMappingTable(Array.isArray(mapRes) ? mapRes : []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCompAnalysis(false);
    }
  };

  const handleDownloadCompetitorVideo = async () => {
    if (!downloadVideoUrl.trim()) return;
    setDownloadingVideo(true);
    setDownloadMessage(null);
    try {
      const res = await downloadCompetitorVideoClient({
        videoUrl: downloadVideoUrl.trim(),
        filenameHint: downloadFilenameHint.trim() || undefined,
        channelId: selectedChannelId,
        competitorHandle: selectedChannel?.competitors[0]?.handle,
      });
      setCompetitorVideos(res.videos);
      const mapRes = await fetchResearchMappingTable();
      setMappingTable(Array.isArray(mapRes) ? mapRes : []);
      setDownloadMessage(
        res.download.status === 'downloaded'
          ? `Đã tải thành công qua ${res.download.tierUsed} vào /data/Competitor_videos`
          : `Trạng thái tải: ${res.download.tierUsed} (${res.download.error || 'Đã ghi nhận'})`,
      );
      setDownloadVideoUrl('');
      setDownloadFilenameHint('');
    } catch (err) {
      setDownloadMessage(err instanceof Error ? err.message : 'Lỗi tải video đối thủ');
    } finally {
      setDownloadingVideo(false);
    }
  };

  const handleReviewOwnVideo = async (video?: OwnGeneratedVideoItem) => {
    const target = video ?? selectedOwnVideo ?? ownVideos[0];
    if (target) setSelectedOwnVideo(target);
    setLoadingOwnReview(true);
    try {
      const res = await reviewOwnGeneratedVideoClient({
        relativePath: target?.relativePath,
        channelId: selectedChannelId,
      });
      setOwnReview(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingOwnReview(false);
    }
  };

  const handleCurateSingleWinProduct = async (
    productNameOverride?: string,
    competitorFileOverride?: string,
  ) => {
    const targetName = (productNameOverride ?? winProductName).trim();
    if (!targetName) return;
    setAcquiringImages(true);
    try {
      const res = await acquireWinProductImagesClient({
        productName: targetName,
        source: winSource,
        competitorVideoFilename: competitorFileOverride ?? (winCompetitorFile || undefined),
        searchQuery: winSearchQuery.trim() || undefined,
        maxImages: 6,
      });
      setLastAcquiredResult(res.result);
      setWinInventory(res.inventory);
      const found = res.inventory.find((x) => x.folderName === targetName);
      if (found) setSelectedWinItem(found);
      const mapRes = await fetchResearchMappingTable();
      setMappingTable(Array.isArray(mapRes) ? mapRes : []);
    } catch (err) {
      console.error(err);
    } finally {
      setAcquiringImages(false);
    }
  };

  const openCreateChannelForm = () => {
    setEditingChannelId(null);
    setNewChannelName('');
    setNewChannelHandle('');
    setNewChannelNiches(['sauce', 'dried', 'snacks']);
    setNewChannelKeywords('');
    setDraftCompetitors([PRESET_COMPETITOR_POOL[0]!, PRESET_COMPETITOR_POOL[1]!]);
    setShowChannelForm(true);
  };

  const openEditChannelForm = (ch: AffiliateChannelProfile) => {
    setEditingChannelId(ch.id);
    setNewChannelName(ch.name);
    setNewChannelHandle(ch.handle);
    setNewChannelNiches(ch.niches?.length ? ch.niches : [ch.niche]);
    setNewChannelKeywords(ch.keywords.join(', '));
    setDraftCompetitors(
      (ch.competitors.length > 0 ? ch.competitors : [PRESET_COMPETITOR_POOL[0]!]).map((comp) => ({
        ...comp,
        niches: getCompNiches(comp),
      })),
    );
    setShowChannelForm(true);
  };

  const toggleDraftNiche = (nicheId: MmoNicheId) => {
    setNewChannelNiches((prev) =>
      prev.includes(nicheId)
        ? prev.length > 1
          ? prev.filter((n) => n !== nicheId)
          : prev
        : [...prev, nicheId],
    );
  };

  const toggleNewCompNiche = (nicheId: MmoNicheId) => {
    setNewCompNiches((prev) =>
      prev.includes(nicheId)
        ? prev.length > 1
          ? prev.filter((n) => n !== nicheId)
          : prev
        : [...prev, nicheId],
    );
  };

  const toggleDraftCompetitorNiche = (compHandle: string, nicheId: MmoNicheId) => {
    setDraftCompetitors((prev) =>
      prev.map((comp) => {
        if (comp.handle.toLowerCase() !== compHandle.toLowerCase()) return comp;
        const current = getCompNiches(comp);
        const nextNiches = current.includes(nicheId)
          ? current.length > 1
            ? current.filter((n) => n !== nicheId)
            : current
          : [...current, nicheId];
        return {
          ...comp,
          niche: nextNiches[0] ?? comp.niche,
          niches: nextNiches,
        };
      }),
    );
  };

  const togglePresetCompetitor = (preset: CompetitorAccountProfile) => {
    setDraftCompetitors((prev) => {
      const exists = prev.some((c) => c.handle.toLowerCase() === preset.handle.toLowerCase());
      if (exists) {
        return prev.filter((c) => c.handle.toLowerCase() !== preset.handle.toLowerCase());
      }
      return [...prev, { ...preset, niches: getCompNiches(preset) }];
    });
  };

  const handleAddCustomCompetitorToDraft = () => {
    if (!newCompHandle.trim()) return;
    const normalizedHandle = newCompHandle.trim().startsWith('@')
      ? newCompHandle.trim()
      : `@${newCompHandle.trim()}`;
    const compNiches = newCompNiches.length > 0 ? newCompNiches : (['sauce'] as MmoNicheId[]);
    const newComp: CompetitorAccountProfile = {
      id: `comp-${Date.now()}`,
      handle: normalizedHandle,
      displayName: newCompName.trim() || normalizedHandle,
      niche: compNiches[0]!,
      niches: compNiches,
      followerEstimate: '65K+',
      avgViewsEstimate: '30K / video',
      hookFormula: newCompHook.trim() || 'Mở đầu cận cảnh sản phẩm + trải nghiệm thực tế 3s đầu',
      strengths: ['Mở đầu trực diện, tốc độ cắt nhanh'],
      weaknesses: ['Bàn ăn chưa đầy đặn chuẩn Abundant Table V6'],
      videoFiles: [],
    };
    setDraftCompetitors((prev) => [
      ...prev.filter((c) => c.handle.toLowerCase() !== normalizedHandle.toLowerCase()),
      newComp,
    ]);
    setNewCompHandle('');
    setNewCompName('');
    setNewCompHook('');
  };

  const handleSaveChannelAndCompetitors = async () => {
    if (!newChannelName.trim()) return;
    try {
      const updated = await saveAffiliateChannel({
        id: editingChannelId ?? undefined,
        name: newChannelName.trim(),
        handle: newChannelHandle.trim() || undefined,
        niche: newChannelNiches[0] ?? 'sauce',
        niches: newChannelNiches,
        keywords: newChannelKeywords
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean),
        competitors: draftCompetitors.map((comp) => {
          const cNiches = getCompNiches(comp);
          return {
            ...comp,
            niche: cNiches[0] ?? comp.niche,
            niches: cNiches,
          };
        }),
      });
      setChannels(updated);
      const targetId = editingChannelId ?? updated[updated.length - 1]?.id;
      if (targetId) setSelectedChannelId(targetId);
      setShowChannelForm(false);
      setEditingChannelId(null);
      setChannelStatusMsg(`✓ Đã lưu cấu hình kênh "${newChannelName.trim()}" (${newChannelNiches.length} ngách, ${draftCompetitors.length} đối thủ).`);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteChannel = async (id: string) => {
    const targetCh = channels.find((c) => c.id === id);
    const remainingOptimistic = channels.filter((c) => c.id !== id);
    // Update state optimistically so UI never jumps or re-adds deleted channels
    setChannels(remainingOptimistic);
    if (selectedChannelId === id) {
      setSelectedChannelId(remainingOptimistic[0]?.id ?? '');
    }
    if (editingChannelId === id) {
      setShowChannelForm(false);
      setEditingChannelId(null);
    }
    try {
      const updated = await deleteAffiliateChannelClient(id);
      setChannels(updated);
      if (selectedChannelId === id) {
        setSelectedChannelId(updated[0]?.id ?? '');
      }
      setChannelStatusMsg(`🗑 Đã xóa vĩnh viễn kênh "${targetCh?.name || id}". (Không tự động khôi phục lại).`);
    } catch (err) {
      console.error(err);
    }
  };

  const handleResetDefaultChannels = async () => {
    try {
      const restored = await resetDefaultAffiliateChannelsClient();
      setChannels(restored);
      if (restored[0]) {
        setSelectedChannelId(restored[0].id);
      }
      setChannelStatusMsg('🔄 Đã khôi phục 3 kênh mẫu mặc định.');
    } catch (err) {
      console.error(err);
    }
  };

  const handleAppendFoodSkill = async () => {
    if (!skillTitle.trim() || !skillContent.trim()) return;
    setUpdatingSkill(true);
    setSkillUpdateNotice(null);
    try {
      const res = await updateFoodSkillsClient({
        title: skillTitle.trim(),
        content: skillContent.trim(),
        dishSlug: skillDishSlug,
        competitorHandle: selectedChannel?.competitors[0]?.handle || '@doithu_tiktok',
      });
      setSkillsAudit(res.audit);
      setSkillUpdateNotice(`Đã cập nhật "${res.addedAtomId}" vào UGC_food_skills & competitors.md!`);
      setSkillTitle('');
      setSkillContent('');
    } catch (err) {
      setSkillUpdateNotice(err instanceof Error ? err.message : 'Lỗi cập nhật UGC_food_skills');
    } finally {
      setUpdatingSkill(false);
    }
  };

  return (
    <div className="youwee-research-hub">
      <div style={{ color: '#f5ebe1', maxWidth: 1480, margin: '0 auto' }}>
      {/* HEADER, MULTI-NICHE / MULTI-COMPETITOR CHANNEL SETUP & AUTOMATION BAR */}
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(34,21,15,0.97), rgba(49,31,21,0.94))',
          border: '1px solid rgba(217,130,43,0.38)',
          borderRadius: 16,
          padding: '18px 20px',
          marginBottom: 18,
          boxShadow: '0 12px 32px rgba(32,16,8,0.24)',
        }}
      >
        <div className="youwee-header-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span
                style={{
                  background: 'rgba(181,68,38,0.24)',
                  color: '#fb923c',
                  border: '1px solid rgba(251,146,60,0.45)',
                  borderRadius: 999,
                  padding: '3px 10px',
                  fontSize: 11,
                  fontWeight: 800,
                }}
              >
                YOUWEE / RESEARCH HUB V2 (AUTO PIPELINE)
              </span>
              <span style={{ fontSize: 12, color: '#cbb59f' }}>
                Kho Video Đối Thủ: <code>/data/Competitor_videos</code> ({competitorVideos.length} video) ·{' '}
                Win Hôm Nay: <code>/data/Products/Today_Win_Products</code> ({winInventory.length} SP) ·{' '}
                Kho Lưu Trữ Win: <code>/data/Products/Win_Products</code>
              </span>
            </div>
            <h2 style={{ margin: '8px 0 4px', fontSize: 20, fontWeight: 800, color: '#fff8ef' }}>
              Trung Tâm Nghiên Cứu Đa Ngách · Đa Đối Thủ · Tự Động Hóa Steps 1 → 4 Từ Today_Win_Products
            </h2>
            <p style={{ margin: 0, fontSize: 12.5, color: '#cbb59f' }}>
              Mỗi kênh (kể cả kênh đối thủ) chọn được <strong>nhiều ngách</strong> và <strong>nhiều đối thủ</strong> · Bảng tra cứu Mapping Video Đối Thủ ↔ Kênh Đối Thủ ↔ Sản Phẩm Win (kèm cột Ngày Đăng &amp; Ghi Chú Trùng Lặp) · So sánh <code>Win_Products</code> để copy nhanh sang <code>Today_Win_Products</code> hoặc tải mới <strong>&gt;= 6 ảnh chuẩn AI Provisioning</strong>.
            </p>
          </div>

          <div className="youwee-action-bar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={openCreateChannelForm}
              style={{
                background: 'rgba(217,130,43,0.2)',
                color: '#fbbf24',
                border: '1px solid rgba(251,191,36,0.48)',
                borderRadius: 10,
                padding: '8px 14px',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              + Thêm Kênh Mới (Đa Ngách &amp; Đa Đối Thủ)
            </button>
            {selectedChannel && (
              <button
                onClick={() => openEditChannelForm(selectedChannel)}
                style={{
                  background: 'rgba(181,68,38,0.22)',
                  color: '#fdba74',
                  border: '1px solid rgba(251,146,60,0.48)',
                  borderRadius: 10,
                  padding: '8px 14px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                ⚙ Sửa Ngách &amp; Đối Thủ Kênh Đang Chọn
              </button>
            )}
            <button
              onClick={() => void handleResetDefaultChannels()}
              style={{
                background: 'rgba(200,125,32,0.16)',
                color: '#fcd34d',
                border: '1px solid rgba(251,191,36,0.38)',
                borderRadius: 10,
                padding: '8px 12px',
                fontSize: 11.5,
                fontWeight: 700,
                cursor: 'pointer',
              }}
              title="Khôi phục lại 3 kênh mẫu nếu bạn đã xóa bớt"
            >
              🔄 Khôi phục 3 kênh mẫu
            </button>
            <button
              onClick={() => void loadInitialData(selectedChannelId, filterByChannel)}
              style={{
                background: 'rgba(71,45,30,0.78)',
                color: '#f5ebe1',
                border: '1px solid rgba(196,154,116,0.34)',
                borderRadius: 10,
                padding: '8px 12px',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Làm mới
            </button>
          </div>
        </div>

        {/* ONE-CLICK AUTOMATION BAR FOR STEPS 1, 2, 3, 4 & RUN ALL */}
        <div
          className="youwee-header-bar"
          style={{
            marginTop: 14,
            padding: '12px 14px',
            borderRadius: 12,
            background: 'linear-gradient(90deg, rgba(181,68,38,0.2), rgba(200,125,32,0.18))',
            border: '1px solid rgba(217,130,43,0.45)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#fbbf24' }}>
              ⚡ CHẠY TỰ ĐỘNG PIPELINE NGHIÊN CỨU (INPUT TỪ TODAY_WIN_PRODUCTS: {winInventory.length} SẢN PHẨM WIN)
            </div>
            <div style={{ fontSize: 11.5, color: '#e6d5c3', marginTop: 2 }}>
              Tự động ánh xạ sản phẩm Win hôm nay ↔ video đối thủ, phân tích đa đối thủ, review video kênh mình, tải &amp; lọc &gt;= 6 ảnh chuẩn AI và cập nhật <code>UGC_food_skills</code>.
            </div>
          </div>
          <div className="youwee-action-bar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => void handleAutoRunPipeline('all')}
              disabled={autoRunningStep !== null}
              style={{
                background: 'linear-gradient(135deg, #b54426, #d9822b)',
                color: '#fffaf2',
                border: 'none',
                borderRadius: 10,
                padding: '9px 16px',
                fontSize: 12.5,
                fontWeight: 900,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(181,68,38,0.35)',
              }}
            >
              {autoRunningStep === 'all' ? 'Đang chạy All Steps 1 → 4...' : '🚀 Chạy Tự Động ALL (Steps 1 → 4)'}
            </button>
            <button
              onClick={() => void handleAutoRunPipeline('step1')}
              disabled={autoRunningStep !== null}
              style={{
                background: 'rgba(34,21,15,0.88)',
                color: '#fdba74',
                border: '1px solid rgba(251,146,60,0.48)',
                borderRadius: 8,
                padding: '7px 11px',
                fontSize: 11.5,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {autoRunningStep === 'step1' ? 'Đang chạy Step 1...' : '⚡ Auto Step 1 (Đối Thủ)'}
            </button>
            <button
              onClick={() => void handleAutoRunPipeline('step2')}
              disabled={autoRunningStep !== null}
              style={{
                background: 'rgba(34,21,15,0.88)',
                color: '#fcd34d',
                border: '1px solid rgba(251,191,36,0.48)',
                borderRadius: 8,
                padding: '7px 11px',
                fontSize: 11.5,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {autoRunningStep === 'step2' ? 'Đang chạy Step 2...' : '⚡ Auto Step 2 (Review Kênh)'}
            </button>
            <button
              onClick={() => void handleAutoRunPipeline('step3')}
              disabled={autoRunningStep !== null}
              style={{
                background: 'rgba(34,21,15,0.88)',
                color: '#fbbf24',
                border: '1px solid rgba(251,191,36,0.48)',
                borderRadius: 8,
                padding: '7px 11px',
                fontSize: 11.5,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {autoRunningStep === 'step3' ? 'Đang lọc >= 6 ảnh...' : '⚡ Auto Step 3 (>= 6 Ảnh Win AI)'}
            </button>
            <button
              onClick={() => void handleAutoRunPipeline('step4')}
              disabled={autoRunningStep !== null}
              style={{
                background: 'rgba(34,21,15,0.88)',
                color: '#f0abfc',
                border: '1px solid rgba(232,121,249,0.45)',
                borderRadius: 8,
                padding: '7px 11px',
                fontSize: 11.5,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {autoRunningStep === 'step4' ? 'Đang chạy Step 4...' : '⚡ Auto Step 4 (Update Skills)'}
            </button>
          </div>
        </div>

        {autoReport && (
          <div
            style={{
              marginTop: 10,
              padding: '10px 14px',
              borderRadius: 10,
              background: 'rgba(200,125,32,0.18)',
              border: '1px solid rgba(251,191,36,0.45)',
              fontSize: 12,
              color: '#fde68a',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <div>
              <strong>✓ Báo cáo Auto Pipeline ({autoReport.executedStep.toUpperCase()}):</strong> {autoReport.summaryVi}
            </div>
            <span style={{ fontSize: 11, color: '#fcd34d' }}>
              Hoàn tất lúc {new Date(autoReport.completedAt).toLocaleTimeString('vi-VN')}
            </span>
          </div>
        )}

        {channelStatusMsg && (
          <div
            style={{
              marginTop: 10,
              padding: '8px 14px',
              borderRadius: 10,
              background: 'rgba(181,68,38,0.18)',
              border: '1px solid rgba(251,146,60,0.45)',
              fontSize: 12,
              color: '#fed7aa',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>{channelStatusMsg}</span>
            <button
              type="button"
              onClick={() => setChannelStatusMsg(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#cbb59f',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* MULTI-CHANNEL CARDS WITH MULTI-NICHE & MULTI-COMPETITOR BADGES */}
        <div className="youwee-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 12, marginTop: 14 }}>
          {channels.map((ch) => {
            const active = ch.id === selectedChannelId;
            const channelNiches = ch.niches?.length ? ch.niches : [ch.niche];
            return (
              <div
                key={ch.id}
                onClick={() => setSelectedChannelId(ch.id)}
                style={{
                  background: active ? 'rgba(181,68,38,0.22)' : 'rgba(28,17,12,0.72)',
                  border: active ? '1px solid #fb923c' : '1px solid rgba(196,154,116,0.24)',
                  borderRadius: 12,
                  padding: '12px 14px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div>
                    <span style={{ fontWeight: 800, fontSize: 14, color: active ? '#fdba74' : '#fff8ef' }}>{ch.name}</span>
                    <div style={{ fontSize: 11.5, color: '#cbb59f', marginTop: 2 }}>
                      Kênh mình: <strong style={{ color: '#f5ebe1' }}>{ch.handle}</strong>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {channelNiches.map((nId) => (
                      <span
                        key={nId}
                        style={{
                          fontSize: 10.5,
                          padding: '2px 7px',
                          borderRadius: 999,
                          background: 'rgba(181,68,38,0.22)',
                          color: '#fdba74',
                          border: '1px solid rgba(251,146,60,0.38)',
                          fontWeight: 700,
                        }}
                      >
                        {nId}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Multiple Competitors Badges (each competitor shows its multiple niches) */}
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: '#e6d5c3', marginBottom: 4 }}>
                    Tập đối thủ theo dõi ({ch.competitors.length} kênh đối thủ · đa ngách):
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {ch.competitors.map((comp) => {
                      const compNiches = getCompNiches(comp);
                      return (
                        <span
                          key={comp.handle}
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: 'rgba(251,191,36,0.14)',
                            color: '#fcd34d',
                            border: '1px solid rgba(251,191,36,0.32)',
                            fontWeight: 600,
                          }}
                        >
                          {comp.handle} ({compNiches.join(', ')})
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div style={{ fontSize: 11, color: '#b59a82', marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>{ch.description}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditChannelForm(ch);
                      }}
                      style={{
                        background: 'rgba(217,130,43,0.18)',
                        border: '1px solid rgba(251,146,60,0.4)',
                        borderRadius: 6,
                        padding: '3px 8px',
                        color: '#fdba74',
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Sửa
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleDeleteChannel(ch.id);
                      }}
                      style={{
                        background: 'rgba(248,113,113,0.16)',
                        border: '1px solid rgba(248,113,113,0.38)',
                        borderRadius: 6,
                        padding: '3px 8px',
                        color: '#fca5a5',
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Xóa kênh
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CREATE / EDIT MULTI-NICHE & MULTI-COMPETITOR CHANNEL FORM */}
        {showChannelForm && (
          <div
            style={{
              marginTop: 14,
              padding: 16,
              borderRadius: 12,
              background: 'rgba(28,17,12,0.96)',
              border: '1px solid rgba(217,130,43,0.5)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#fbbf24' }}>
                {editingChannelId
                  ? `Cấu Hình Đa Ngách & Đa Đối Thủ Cho Kênh: ${newChannelName}`
                  : 'Tạo Kênh TikTok Mới (Chọn Nhiều Ngách Cho Kênh Mình & Nhiều Ngách Cho Từng Kênh Đối Thủ)'}
              </div>
              <button
                type="button"
                onClick={() => setShowAll20Niches((v) => !v)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fdba74',
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {showAll20Niches ? 'Thu gọn danh sách ngách' : '+ Hiển thị đủ 20 ngách Ông Chú MMO'}
              </button>
            </div>

            <div className="youwee-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 12 }}>
              <input
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
                placeholder="Tên kênh (VD: Kênh 4 · Đa Ngách Đồ Khô & Gia Vị)"
                style={{ padding: '8px 10px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12 }}
              />
              <input
                value={newChannelHandle}
                onChange={(e) => setNewChannelHandle(e.target.value)}
                placeholder="ID kênh mình (VD: @bep_dac_san_vn)"
                style={{ padding: '8px 10px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12 }}
              />
              <input
                value={newChannelKeywords}
                onChange={(e) => setNewChannelKeywords(e.target.value)}
                placeholder="Từ khóa nhận diện video (cách nhau dấu phẩy)"
                style={{ padding: '8px 10px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12 }}
              />
            </div>

            {/* MULTI-NICHE SELECTOR FOR OWN CHANNEL */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#e6d5c3', marginBottom: 6 }}>
                1. Chọn Nhiều Ngách Cho Kênh Mình (Đã chọn {newChannelNiches.length} ngách: {newChannelNiches.join(', ')}):
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {NICHE_OPTIONS.filter((n) => showAll20Niches || n.group === 'food' || newChannelNiches.includes(n.id)).map((n) => {
                  const active = newChannelNiches.includes(n.id);
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => toggleDraftNiche(n.id)}
                      style={{
                        padding: '5px 10px',
                        borderRadius: 8,
                        fontSize: 11.5,
                        fontWeight: 700,
                        cursor: 'pointer',
                        background: active ? '#b54426' : 'rgba(49,31,21,0.85)',
                        color: active ? '#fffaf2' : '#e6d5c3',
                        border: active ? '1px solid #fb923c' : '1px solid #5c3d28',
                      }}
                    >
                      {active ? '✓ ' : '+ '}
                      {n.label} ({n.id})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* MULTI-COMPETITOR SELECTOR & MULTI-NICHE PER COMPETITOR BUILDER */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#e6d5c3', marginBottom: 6 }}>
                2. Chọn Hoặc Thêm Nhiều Kênh Đối Thủ — Mỗi Kênh Đối Thủ Chọn Được Nhiều Ngách ({draftCompetitors.length} đối thủ được gắn):
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {PRESET_COMPETITOR_POOL.map((preset) => {
                  const selected = draftCompetitors.some((c) => c.handle.toLowerCase() === preset.handle.toLowerCase());
                  const presetNiches = getCompNiches(preset);
                  return (
                    <button
                      key={preset.handle}
                      type="button"
                      onClick={() => togglePresetCompetitor(preset)}
                      style={{
                        padding: '5px 10px',
                        borderRadius: 8,
                        fontSize: 11.5,
                        fontWeight: 700,
                        cursor: 'pointer',
                        background: selected ? 'rgba(251,191,36,0.22)' : 'rgba(49,31,21,0.85)',
                        color: selected ? '#fcd34d' : '#cbb59f',
                        border: selected ? '1px solid #fbbf24' : '1px solid #5c3d28',
                      }}
                    >
                      {selected ? '✓ ' : '+ '}
                      {preset.displayName} ({preset.handle} · Ngách: {presetNiches.join(', ')})
                    </button>
                  );
                })}
              </div>

              {/* Add custom competitor with multi-niche selection */}
              <div
                style={{
                  padding: 12,
                  borderRadius: 10,
                  background: 'rgba(49,31,21,0.65)',
                  border: '1px solid rgba(217,130,43,0.34)',
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#fdba74', marginBottom: 8 }}>
                  + Thêm Kênh Đối Thủ Mới (Chọn Nhiều Ngách Cho Kênh Đối Thủ Này):
                </div>
                <div className="youwee-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr auto', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <input
                    value={newCompHandle}
                    onChange={(e) => setNewCompHandle(e.target.value)}
                    placeholder="ID đối thủ thêm mới (VD: @bep_taybac)"
                    style={{ padding: '7px 10px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12 }}
                  />
                  <input
                    value={newCompName}
                    onChange={(e) => setNewCompName(e.target.value)}
                    placeholder="Tên hiển thị đối thủ"
                    style={{ padding: '7px 10px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12 }}
                  />
                  <input
                    value={newCompHook}
                    onChange={(e) => setNewCompHook(e.target.value)}
                    placeholder="Công thức Hook của đối thủ (VD: Cận cảnh rưới sốt 2s đầu)"
                    style={{ padding: '7px 10px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12 }}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomCompetitorToDraft}
                    style={{
                      background: '#d9822b',
                      color: '#1e120a',
                      border: 'none',
                      borderRadius: 8,
                      padding: '7px 12px',
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    + Thêm Đối Thủ ({newCompNiches.length} ngách)
                  </button>
                </div>

                <div style={{ fontSize: 11, color: '#cbb59f', marginBottom: 5 }}>
                  Chọn nhiều ngách cho đối thủ mới ({newCompNiches.join(', ')}):
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {NICHE_OPTIONS.filter((n) => showAll20Niches || n.group === 'food' || newCompNiches.includes(n.id)).map((n) => {
                    const active = newCompNiches.includes(n.id);
                    return (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => toggleNewCompNiche(n.id)}
                        style={{
                          padding: '3px 9px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                          background: active ? 'rgba(251,191,36,0.25)' : 'rgba(28,17,12,0.88)',
                          color: active ? '#fde68a' : '#cbb59f',
                          border: active ? '1px solid #fbbf24' : '1px solid #5c3d28',
                        }}
                      >
                        {active ? '✓ ' : '+ '}
                        {n.label} ({n.id})
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* List of currently attached competitors in draft with interactive multi-niche pills per competitor */}
              {draftCompetitors.length > 0 && (
                <div className="youwee-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 8, marginTop: 8 }}>
                  {draftCompetitors.map((c) => {
                    const compNiches = getCompNiches(c);
                    return (
                      <div
                        key={c.handle}
                        style={{
                          padding: '8px 10px',
                          borderRadius: 10,
                          background: 'rgba(200,125,32,0.12)',
                          border: '1px solid rgba(217,130,43,0.38)',
                          fontSize: 11.5,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <div>
                            <strong style={{ color: '#fcd34d' }}>{c.handle}</strong>{' '}
                            <span style={{ color: '#e6d5c3' }}>({c.displayName})</span>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setDraftCompetitors((prev) => prev.filter((x) => x.handle !== c.handle))
                            }
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#fca5a5',
                              cursor: 'pointer',
                              fontWeight: 800,
                              fontSize: 11,
                            }}
                          >
                            ✕ Gỡ đối thủ
                          </button>
                        </div>
                        <div style={{ fontSize: 10.5, color: '#cbb59f', marginBottom: 4 }}>
                          Ngách của đối thủ này (bấm để chọn/bỏ nhiều ngách):
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {NICHE_OPTIONS.filter((n) => showAll20Niches || n.group === 'food' || compNiches.includes(n.id)).map((n) => {
                            const active = compNiches.includes(n.id);
                            return (
                              <button
                                key={n.id}
                                type="button"
                                onClick={() => toggleDraftCompetitorNiche(c.handle, n.id)}
                                style={{
                                  padding: '2px 7px',
                                  borderRadius: 999,
                                  fontSize: 10.5,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  background: active ? 'rgba(251,191,36,0.22)' : 'rgba(28,17,12,0.8)',
                                  color: active ? '#fcd34d' : '#b59a82',
                                  border: active ? '1px solid #fbbf24' : '1px solid #5c3d28',
                                }}
                              >
                                {active ? '✓ ' : '+ '}
                                {n.id}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="youwee-action-bar" style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => void handleSaveChannelAndCompetitors()}
                style={{
                  background: 'linear-gradient(135deg, #b54426, #d9822b)',
                  color: '#fffaf2',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 12.5,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Lưu Kênh ({newChannelNiches.length} Ngách &amp; {draftCompetitors.length} Đối Thủ Đa Ngách)
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowChannelForm(false);
                  setEditingChannelId(null);
                }}
                style={{
                  background: 'transparent',
                  color: '#cbb59f',
                  border: '1px solid #5c3d28',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Đóng
              </button>
            </div>
          </div>
        )}
      </div>

      {/* BẢNG TRA CỨU MAPPING: VIDEO ĐỐI THỦ ↔ TÊN KÊNH ĐỐI THỦ ↔ TÊN SẢN PHẨM WIN ↔ NGÀY ĐĂNG & TRÙNG LẶP */}
      <div
        style={{
          background: 'rgba(34,21,15,0.92)',
          border: '1px solid rgba(251,191,36,0.38)',
          borderRadius: 14,
          padding: '14px 18px',
          marginBottom: 18,
        }}
      >
        <div className="youwee-header-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
          <div>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#fbbf24' }}>
              BẢNG TRA CỨU MAPPING TỰ ĐỘNG (COMPETITOR VIDEOS ↔ KÊNH ĐỐI THỦ ↔ TODAY_WIN_PRODUCTS)
            </span>
            <h3 style={{ margin: '3px 0 0', fontSize: 15, color: '#fff8ef' }}>
              Danh Sách Ánh Xạ Video Đối Thủ (<code>/data/Competitor_videos</code>) Với Kênh Đối Thủ, Sản Phẩm Win Hôm Nay &amp; Ghi Chú Ngày Đăng Trùng Lặp ({mappingTable.length} bản ghi)
            </h3>
          </div>
          <div className="youwee-action-bar" style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => void handleAutoRunPipeline('step3')}
              disabled={autoRunningStep !== null}
              style={{
                background: '#d9822b',
                color: '#1e120a',
                border: 'none',
                borderRadius: 8,
                padding: '7px 13px',
                fontSize: 12,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              {autoRunningStep === 'step3'
                ? 'Đang tự động tải & lọc >= 6 ảnh...'
                : '⚡ Tự Động Tải & Lọc >= 6 Ảnh Cho Toàn Bộ Sản Phẩm Trong Bảng'}
            </button>
          </div>
        </div>

        <div className="youwee-table-scroll" style={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto' }}>
          <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'rgba(54,34,22,0.95)', color: '#d6bfa9', textAlign: 'left' }}>
                <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(196,154,116,0.24)' }}>Video Đối Thủ (/Competitor_videos)</th>
                <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(196,154,116,0.24)' }}>Tên Kênh Đối Thủ</th>
                <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(196,154,116,0.24)' }}>Tên Sản Phẩm Win (Today_Win_Products)</th>
                <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(196,154,116,0.24)' }}>Ngách</th>
                <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(196,154,116,0.24)' }}>Date / Ghi Chú Trùng Lặp Sản Phẩm</th>
                <th style={{ padding: '8px 10px', borderBottom: '1px solid rgba(196,154,116,0.24)', textAlign: 'right' }}>Ảnh AI Provision &amp; Thao Tác</th>
              </tr>
            </thead>
            <tbody>
              {mappingTable.map((row) => {
                const hasRealVideo = row.videoFilename !== '(Chưa có video đối thủ khớp trực tiếp)';
                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: '1px solid rgba(196,154,116,0.14)',
                      background: row.isDuplicateProduct ? 'rgba(251,191,36,0.08)' : 'transparent',
                    }}
                  >
                    <td style={{ padding: '8px 10px', fontFamily: 'monospace', color: hasRealVideo ? '#fdba74' : '#9c826b' }}>
                      {row.videoFilename}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <strong style={{ color: '#fff8ef' }}>{row.competitorHandle}</strong>
                      <div style={{ fontSize: 11, color: '#cbb59f' }}>{row.competitorDisplayName}</div>
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ fontWeight: 700, color: '#fff8ef' }}>
                        {row.productName}
                        {row.isDiyCookingProduct && (
                          <span
                            style={{
                              marginLeft: 6,
                              padding: '1px 6px',
                              borderRadius: 6,
                              background: 'rgba(251,146,60,0.22)',
                              color: '#fb923c',
                              fontSize: 10.5,
                              fontWeight: 800,
                            }}
                          >
                            DIY Nấu Ăn
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#cbb59f' }}>
                        Thư mục: <code>{row.winProductFolder}</code>
                      </div>
                    </td>
                    <td style={{ padding: '8px 10px', color: '#e6d5c3' }}>
                      {row.nicheLabel} (<code>{row.niche}</code>)
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: row.isDuplicateProduct ? 'rgba(251,191,36,0.22)' : 'rgba(54,34,22,0.75)',
                          color: row.isDuplicateProduct ? '#fde68a' : '#e6d5c3',
                          border: row.isDuplicateProduct ? '1px solid rgba(251,191,36,0.48)' : '1px solid transparent',
                          fontSize: 11,
                          fontWeight: row.isDuplicateProduct ? 700 : 500,
                        }}
                      >
                        📅 {row.publishedDate} · {row.duplicateNote}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <span
                        style={{
                          marginRight: 8,
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: row.curatedImageCount >= 6 ? 'rgba(217,130,43,0.22)' : 'rgba(244,63,94,0.2)',
                          color: row.curatedImageCount >= 6 ? '#fcd34d' : '#fb7185',
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {row.curatedImageCount} ảnh đã kiểm định
                      </span>
                      <button
                        onClick={() => {
                          setWinProductName(row.winProductFolder || row.productName);
                          if (hasRealVideo && row.videoFilename) setWinCompetitorFile(row.videoFilename);
                          setSubTab('win_images');
                          void handleCurateSingleWinProduct(
                            row.winProductFolder || row.productName,
                            hasRealVideo && row.videoFilename ? row.videoFilename : undefined,
                          );
                        }}
                        style={{
                          background: 'rgba(217,130,43,0.22)',
                          color: '#fcd34d',
                          border: '1px solid rgba(251,191,36,0.48)',
                          borderRadius: 6,
                          padding: '4px 8px',
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                          marginRight: 6,
                        }}
                      >
                        Lọc &gt;= 6 Ảnh
                      </button>
                      {hasRealVideo && (
                        <button
                          onClick={() => {
                            const foundVid = competitorVideos.find((v) => v.filename === row.videoFilename);
                            if (foundVid) {
                              setSubTab('competitors');
                              void handleAnalyzeCompetitorVideo(foundVid, false);
                            }
                          }}
                          style={{
                            background: 'rgba(181,68,38,0.24)',
                            color: '#fdba74',
                            border: '1px solid rgba(251,146,60,0.45)',
                            borderRadius: 6,
                            padding: '4px 8px',
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          Phân Tích Video
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* SUB-NAVIGATION TABS */}
      <div className="youwee-header-bar" style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="youwee-action-bar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { id: 'competitors', label: `Step 1. Kho Video & Phân Tích Đa Đối Thủ (${competitorVideos.length})` },
            { id: 'own_review', label: `Step 2. Review Video Kênh Mình Mới Nhất (${ownVideos.length})` },
            { id: 'win_images', label: `Step 3. Tải & Lọc >= 6 Ảnh Today_Win_Products (${winInventory.length} SP)` },
            { id: 'food_skills', label: `Step 4. Review & Update UGC_food_skills (KBS_RAG_MCP)` },
            { id: 'classic_youwee', label: '5. Youwee Tải Đa Nền Tảng' },
          ].map((t) => {
            const active = subTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setSubTab(t.id as HubSubTab)}
                style={{
                  background: active ? 'linear-gradient(135deg, #b54426, #d9822b)' : 'rgba(43,27,18,0.88)',
                  color: active ? '#fffaf2' : '#e6d5c3',
                  border: active ? '1px solid #fb923c' : '1px solid rgba(196,154,116,0.26)',
                  borderRadius: 10,
                  padding: '9px 15px',
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#3d281c', fontWeight: 700, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={filterByChannel}
            onChange={(e) => setFilterByChannel(e.target.checked)}
          />
          Chỉ lọc video theo kênh đang chọn ({selectedChannel?.name})
        </label>
      </div>

      {/* SUB-TAB 1: COMPETITOR VIDEOS & MULTI-NICHE / MULTI-COMPETITOR ANALYSIS */}
      {subTab === 'competitors' && (
        <div className="youwee-split-grid" style={{ display: 'grid', gridTemplateColumns: '1.05fr 1.25fr', gap: 18 }}>
          {/* LEFT: COMPETITOR VIDEO LIBRARY + PLAYER + DOWNLOADER */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div
              style={{
                background: 'rgba(34,21,15,0.9)',
                border: '1px solid rgba(196,154,116,0.26)',
                borderRadius: 14,
                padding: 16,
              }}
            >
              <div className="youwee-header-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#fff8ef' }}>
                  Tải Thêm Video Đối Thủ Về <code>/data/Competitor_videos</code>
                </div>
                <button
                  onClick={() => void handleAutoRunPipeline('step1')}
                  disabled={autoRunningStep !== null}
                  style={{
                    background: 'rgba(217,130,43,0.22)',
                    color: '#fdba74',
                    border: '1px solid rgba(251,146,60,0.48)',
                    borderRadius: 8,
                    padding: '5px 10px',
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {autoRunningStep === 'step1' ? 'Đang chạy Auto Step 1...' : '⚡ Chạy Tự Động Step 1 (Từ Today_Win_Products)'}
                </button>
              </div>
              <div className="youwee-action-bar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  value={downloadVideoUrl}
                  onChange={(e) => setDownloadVideoUrl(e.target.value)}
                  placeholder="Dán link TikTok / Douyin / YouTube Shorts của đối thủ..."
                  style={{ flex: 2, minWidth: 200, padding: '8px 10px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12 }}
                />
                <input
                  value={downloadFilenameHint}
                  onChange={(e) => setDownloadFilenameHint(e.target.value)}
                  placeholder="Tên gợi ý (VD: sot_cham_hai_san)"
                  style={{ flex: 1, minWidth: 140, padding: '8px 10px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12 }}
                />
                <button
                  onClick={() => void handleDownloadCompetitorVideo()}
                  disabled={downloadingVideo}
                  style={{
                    background: '#d9822b',
                    color: '#1e120a',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 14px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {downloadingVideo ? 'Đang tải...' : 'Tải về Kho Đối Thủ'}
                </button>
              </div>
              {downloadMessage && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#fdba74' }}>{downloadMessage}</div>
              )}
            </div>

            {/* VIDEO PLAYER & LIST */}
            <div
              style={{
                background: 'rgba(34,21,15,0.9)',
                border: '1px solid rgba(196,154,116,0.26)',
                borderRadius: 14,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#fff8ef' }}>
                  Kho Video Đối Thủ ({competitorVideos.length} tệp trong <code>Competitor_videos</code>)
                </div>
              </div>

              {selectedCompetitorVideo && (
                <div
                  className="youwee-grid-2"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '180px 1fr',
                    gap: 14,
                    padding: 12,
                    borderRadius: 12,
                    background: 'rgba(20,12,8,0.82)',
                    border: '1px solid rgba(217,130,43,0.38)',
                    marginBottom: 12,
                  }}
                >
                  <video
                    key={selectedCompetitorVideo.streamUrl}
                    src={selectedCompetitorVideo.streamUrl}
                    controls
                    style={{ width: '100%', maxHeight: 280, borderRadius: 8, background: '#0e0805' }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#fb923c', fontWeight: 700 }}>
                        ĐANG XEM VIDEO ĐỐI THỦ ({selectedCompetitorVideo.matchedCompetitorHandle})
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#fff8ef', marginTop: 4 }}>
                        {selectedCompetitorVideo.inferredProductName}
                      </div>
                      <div style={{ fontSize: 12, color: '#cbb59f', marginTop: 4 }}>
                        Tệp: <code>{selectedCompetitorVideo.filename}</code>
                      </div>
                      <div style={{ fontSize: 12, color: '#e6d5c3', marginTop: 6 }}>
                        Thời lượng: <strong>{selectedCompetitorVideo.durationSec}s</strong> · Khung hình:{' '}
                        <strong>
                          {selectedCompetitorVideo.width}x{selectedCompetitorVideo.height}
                        </strong>{' '}
                        · Nhóm món KB: <strong style={{ color: '#fcd34d' }}>{selectedCompetitorVideo.dishName} ({selectedCompetitorVideo.dishSlug})</strong>
                      </div>
                    </div>

                    <div className="youwee-action-bar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                      <button
                        onClick={() => void handleAnalyzeCompetitorVideo(selectedCompetitorVideo, false)}
                        disabled={loadingCompAnalysis}
                        style={{
                          background: '#b54426',
                          color: '#fffaf2',
                          border: 'none',
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Phân Tích Sâu Video Này
                      </button>
                      <button
                        onClick={() => void handleAnalyzeCompetitorVideo(selectedCompetitorVideo, true)}
                        disabled={loadingCompAnalysis}
                        style={{
                          background: 'rgba(217,130,43,0.22)',
                          color: '#fcd34d',
                          border: '1px solid rgba(251,191,36,0.5)',
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Lọc &gt;= 6 Ảnh SP Win Sang /Today_Win_Products
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ maxHeight: 330, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {competitorVideos.map((v) => {
                  const isSel = selectedCompetitorVideo?.filename === v.filename;
                  return (
                    <div
                      key={v.filename}
                      onClick={() => void handleAnalyzeCompetitorVideo(v, false)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: isSel ? 'rgba(181,68,38,0.24)' : 'rgba(49,31,21,0.6)',
                        border: isSel ? '1px solid #fb923c' : '1px solid rgba(196,154,116,0.18)',
                        cursor: 'pointer',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#fff8ef' }}>{v.inferredProductName}</div>
                        <div style={{ fontSize: 11, color: '#cbb59f' }}>
                          <code>{v.filename}</code> · {v.matchedCompetitorHandle} · Món: {v.dishName}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', fontSize: 11, color: '#e6d5c3' }}>
                        <div>{v.durationSec}s</div>
                        <div style={{ color: '#b59a82' }}>{(v.sizeBytes / (1024 * 1024)).toFixed(1)} MB</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT: MULTI-NICHE & MULTI-COMPETITOR EVALUATION */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* MULTI-NICHE & MULTI-COMPETITOR FILTER PILLS FOR CURRENT CHANNEL */}
            {selectedChannel && (
              <div
                style={{
                  background: 'rgba(34,21,15,0.9)',
                  border: '1px solid rgba(217,130,43,0.34)',
                  borderRadius: 14,
                  padding: 14,
                }}
              >
                <div className="youwee-header-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#fdba74' }}>
                    BỘ LỌC ĐA NGÁCH &amp; ĐA ĐỐI THỦ CỦA KÊNH ({selectedChannel.name})
                  </span>
                  <button
                    onClick={() => void handleAnalyzeCompetitorChannel(selectedChannel.id, selectedEvalNiches, selectedEvalCompetitors)}
                    style={{
                      background: '#b54426',
                      color: '#fffaf2',
                      border: 'none',
                      borderRadius: 6,
                      padding: '4px 10px',
                      fontSize: 11,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    Đánh Giá Lại Theo Bộ Lọc
                  </button>
                </div>

                <div style={{ fontSize: 11.5, color: '#e6d5c3', marginBottom: 4 }}>
                  Chọn các ngách cần phân tích ({selectedEvalNiches.length} ngách):
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  {(selectedChannel.niches?.length ? selectedChannel.niches : [selectedChannel.niche]).map((nId) => {
                    const on = selectedEvalNiches.includes(nId);
                    return (
                      <button
                        key={nId}
                        type="button"
                        onClick={() =>
                          setSelectedEvalNiches((prev) =>
                            prev.includes(nId) ? (prev.length > 1 ? prev.filter((x) => x !== nId) : prev) : [...prev, nId],
                          )
                        }
                        style={{
                          padding: '3px 9px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                          background: on ? 'rgba(181,68,38,0.28)' : 'rgba(49,31,21,0.75)',
                          color: on ? '#fdba74' : '#cbb59f',
                          border: on ? '1px solid #fb923c' : '1px solid #5c3d28',
                        }}
                      >
                        {on ? '✓ ' : ''}Ngách: {nId}
                      </button>
                    );
                  })}
                </div>

                <div style={{ fontSize: 11.5, color: '#e6d5c3', marginBottom: 4 }}>
                  Chọn các đối thủ cần so kè ({selectedEvalCompetitors.length}/{selectedChannel.competitors.length} đối thủ):
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {selectedChannel.competitors.map((comp) => {
                    const on = selectedEvalCompetitors.includes(comp.handle);
                    return (
                      <button
                        key={comp.handle}
                        type="button"
                        onClick={() =>
                          setSelectedEvalCompetitors((prev) =>
                            prev.includes(comp.handle)
                              ? prev.length > 1
                                ? prev.filter((x) => x !== comp.handle)
                                : prev
                              : [...prev, comp.handle],
                          )
                        }
                        style={{
                          padding: '3px 9px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                          background: on ? 'rgba(251,191,36,0.24)' : 'rgba(49,31,21,0.75)',
                          color: on ? '#fde68a' : '#cbb59f',
                          border: on ? '1px solid #fbbf24' : '1px solid #5c3d28',
                        }}
                      >
                        {on ? '✓ ' : ''}
                        {comp.displayName} ({comp.handle} · {getCompNiches(comp).join(', ')})
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CHANNEL COMPETITOR STRATEGY EVALUATION */}
            {channelEval && (
              <div
                style={{
                  background: 'rgba(34,21,15,0.9)',
                  border: '1px solid rgba(251,191,36,0.34)',
                  borderRadius: 14,
                  padding: 16,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#fbbf24' }}>
                    PHÂN TÍCH ĐÁNH GIÁ ĐA ĐỐI THỦ ({channelEval.nicheLabel.toUpperCase()})
                  </span>
                  <span style={{ fontSize: 11, color: '#cbb59f' }}>{channelEval.channelName}</span>
                </div>
                <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#f5ebe1', lineHeight: 1.5 }}>
                  {channelEval.positioningSummary}
                </p>

                {channelEval.evaluatedCompetitors.map((comp) => (
                  <div
                    key={comp.handle}
                    style={{
                      padding: 10,
                      borderRadius: 10,
                      background: 'rgba(54,34,22,0.72)',
                      border: '1px solid rgba(196,154,116,0.22)',
                      marginBottom: 10,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, fontSize: 12.5, fontWeight: 700 }}>
                      <span style={{ color: '#fdba74' }}>
                        Đối thủ: {comp.displayName} ({comp.handle} · Ngách: {getCompNiches(comp).join(', ')})
                      </span>
                      <span style={{ color: '#fbbf24' }}>
                        Follower: {comp.followerEstimate} · View TB: {comp.avgViewsEstimate} · Mức cạnh tranh: {comp.threatLevel}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#e6d5c3', marginTop: 4 }}>
                      <strong>Công thức Hook:</strong> {comp.hookFormula}
                    </div>
                  </div>
                ))}

                <div className="youwee-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ background: 'rgba(200,125,32,0.12)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 10, padding: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#fcd34d', marginBottom: 6 }}>
                      Điểm Mạnh Của Tập Đối Thủ Cần Học Hỏi
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#e6d5c3', lineHeight: 1.5 }}>
                      {channelEval.winningPatterns.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  </div>
                  <div style={{ background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.28)', borderRadius: 10, padding: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#fda4af', marginBottom: 6 }}>
                      Điểm Yếu Của Tập Đối Thủ Để Kênh Mình Vượt Mặt
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#e6d5c3', lineHeight: 1.5 }}>
                      {channelEval.exploitableGaps.map((g, i) => (
                        <li key={i}>{g}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* INDIVIDUAL COMPETITOR VIDEO EVALUATION */}
            {videoEval ? (
              <div
                style={{
                  background: 'rgba(34,21,15,0.9)',
                  border: '1px solid rgba(217,130,43,0.38)',
                  borderRadius: 14,
                  padding: 16,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#fb923c' }}>
                      Bóc Tách Chi Tiết Video Đối Thủ: {videoEval.filename}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#fff8ef' }}>
                      {videoEval.inferredProductName} ({videoEval.durationSec}s · {videoEval.resolution})
                    </div>
                  </div>
                  <div
                    style={{
                      background: 'rgba(217,130,43,0.22)',
                      color: '#fcd34d',
                      border: '1px solid rgba(251,191,36,0.48)',
                      borderRadius: 10,
                      padding: '6px 12px',
                      fontWeight: 800,
                      fontSize: 14,
                    }}
                  >
                    Điểm Viral: {videoEval.scores.compositeScore}/10
                  </div>
                </div>

                {/* 5-AXIS SCORES */}
                <div className="youwee-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 12 }}>
                  {[
                    { label: 'Hook 3s Đầu', val: videoEval.scores.hookStrength },
                    { label: 'Rõ Sản Phẩm', val: videoEval.scores.productClarity },
                    { label: 'Độ Thèm Ăn', val: videoEval.scores.sensoryPayoff },
                    { label: 'Nhịp Độ Cắt', val: videoEval.scores.scriptPacing },
                    { label: 'Thúc Đẩy Mua', val: videoEval.scores.ctaUrgency },
                  ].map((s) => (
                    <div
                      key={s.label}
                      style={{
                        background: 'rgba(54,34,22,0.75)',
                        borderRadius: 8,
                        padding: '8px 6px',
                        textAlign: 'center',
                        border: '1px solid rgba(196,154,116,0.22)',
                      }}
                    >
                      <div style={{ fontSize: 11, color: '#cbb59f' }}>{s.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#fff8ef', marginTop: 2 }}>{s.val}/10</div>
                    </div>
                  ))}
                </div>

                {/* TIMESTAMP BEATS */}
                <div style={{ fontSize: 12, fontWeight: 700, color: '#f5ebe1', marginBottom: 6 }}>
                  Cấu Trúc Nhịp Giây (Timestamp Beats):
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  {videoEval.timestampBeats.map((b) => (
                    <div
                      key={b.range}
                      style={{
                        padding: '7px 10px',
                        borderRadius: 8,
                        background: 'rgba(54,34,22,0.58)',
                        fontSize: 12,
                      }}
                    >
                      <strong style={{ color: '#fdba74' }}>{b.range} · {b.phase}:</strong>{' '}
                      <span style={{ color: '#e6d5c3' }}>{b.observation}</span>
                    </div>
                  ))}
                </div>

                {/* HOW TO BEAT */}
                <div
                  style={{
                    padding: 10,
                    borderRadius: 10,
                    background: 'rgba(181,68,38,0.16)',
                    border: '1px solid rgba(251,146,60,0.35)',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#fdba74', marginBottom: 4 }}>
                    Chiến Thuật Để Video Kênh Mình Thắng Video Này:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#f5ebe1', lineHeight: 1.5 }}>
                    {videoEval.howToBeat.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div
                style={{
                  background: 'rgba(34,21,15,0.75)',
                  border: '1px dashed rgba(196,154,116,0.38)',
                  borderRadius: 14,
                  padding: 24,
                  textAlign: 'center',
                  color: '#cbb59f',
                  fontSize: 13,
                }}
              >
                Bấm vào bất kỳ video đối thủ nào ở cột bên trái hoặc bấm <strong>"⚡ Auto Step 1"</strong> để tự động phân tích toàn bộ video đối thủ gắn với Sản Phẩm Win.
              </div>
            )}
          </div>
        </div>
      )}

      {/* SUB-TAB 2: REVIEW LATEST GENERATED VIDEO OF OUR CHANNEL */}
      {subTab === 'own_review' && (
        <div className="youwee-split-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 18 }}>
          <div
            style={{
              background: 'rgba(34,21,15,0.9)',
              border: '1px solid rgba(196,154,116,0.26)',
              borderRadius: 14,
              padding: 16,
            }}
          >
            <div className="youwee-header-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#fff8ef' }}>
                  Video Generated Mới Nhất Kênh Mình (<code>/data/merged_video</code>)
                </div>
                <div style={{ fontSize: 11.5, color: '#cbb59f' }}>
                  Chọn video hoặc bấm Auto Step 2 để chấm điểm đối kháng với đối thủ theo sản phẩm Win
                </div>
              </div>
              <div className="youwee-action-bar" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  onClick={() => void handleAutoRunPipeline('step2')}
                  disabled={autoRunningStep !== null}
                  style={{
                    background: 'rgba(181,68,38,0.22)',
                    color: '#fdba74',
                    border: '1px solid rgba(251,146,60,0.45)',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {autoRunningStep === 'step2' ? 'Đang chạy Auto Step 2...' : '⚡ Auto Step 2'}
                </button>
                <button
                  onClick={() => void handleReviewOwnVideo(selectedOwnVideo ?? ownVideos[0])}
                  disabled={loadingOwnReview}
                  style={{
                    background: '#d9822b',
                    color: '#1e120a',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 14px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {loadingOwnReview ? 'Đang chấm...' : 'Review Video Đang Chọn'}
                </button>
              </div>
            </div>

            {selectedOwnVideo && (
              <div
                className="youwee-grid-2"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '170px 1fr',
                  gap: 12,
                  padding: 12,
                  borderRadius: 12,
                  background: 'rgba(20,12,8,0.84)',
                  border: '1px solid rgba(217,130,43,0.38)',
                  marginBottom: 12,
                }}
              >
                <video
                  key={selectedOwnVideo.streamUrl}
                  src={selectedOwnVideo.streamUrl}
                  controls
                  style={{ width: '100%', maxHeight: 260, borderRadius: 8, background: '#0e0805' }}
                />
                <div>
                  <span style={{ fontSize: 11, color: '#fcd34d', fontWeight: 700 }}>
                    VIDEO MỚI NHẤT · NGÀY {selectedOwnVideo.dateFolder}
                  </span>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff8ef', marginTop: 4 }}>
                    {selectedOwnVideo.inferredProductName}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#cbb59f', marginTop: 4, wordBreak: 'break-all' }}>
                    <code>{selectedOwnVideo.relativePath}</code>
                  </div>
                  <div style={{ fontSize: 12, color: '#e6d5c3', marginTop: 8 }}>
                    Kênh phụ trách: <strong>{selectedOwnVideo.matchedChannelName}</strong>
                  </div>
                  <div style={{ fontSize: 12, color: '#e6d5c3', marginTop: 4 }}>
                    Nhóm món: <strong style={{ color: '#fdba74' }}>{selectedOwnVideo.dishName}</strong> · Dung lượng:{' '}
                    {(selectedOwnVideo.sizeBytes / (1024 * 1024)).toFixed(1)} MB
                  </div>
                </div>
              </div>
            )}

            <div style={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ownVideos.map((v) => {
                const isSel = selectedOwnVideo?.relativePath === v.relativePath;
                return (
                  <div
                    key={v.relativePath}
                    onClick={() => void handleReviewOwnVideo(v)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: isSel ? 'rgba(217,130,43,0.22)' : 'rgba(49,31,21,0.6)',
                      border: isSel ? '1px solid #fbbf24' : '1px solid rgba(196,154,116,0.18)',
                      cursor: 'pointer',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#fff8ef' }}>{v.inferredProductName}</div>
                      <div style={{ fontSize: 11, color: '#cbb59f' }}>
                        Ngày {v.dateFolder} · {v.matchedChannelName}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#e6d5c3', textAlign: 'right' }}>
                      <div>{(v.sizeBytes / (1024 * 1024)).toFixed(1)} MB</div>
                      <div style={{ color: '#fcd34d' }}>Chấm điểm →</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT: OWN VIDEO REVIEW REPORT */}
          <div>
            {ownReview ? (
              <div
                style={{
                  background: 'rgba(34,21,15,0.9)',
                  border: '1px solid rgba(217,130,43,0.38)',
                  borderRadius: 14,
                  padding: 18,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#fcd34d' }}>
                      BÁO CÁO REVIEW VIDEO GENERATED CỦA KÊNH ({ownReview.channel.name})
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#fff8ef' }}>
                      {ownReview.video.inferredProductName} ({ownReview.video.durationSec}s · {ownReview.video.width}x{ownReview.video.height})
                    </div>
                  </div>
                  <div
                    style={{
                      background: ownReview.scores.passedGate ? 'rgba(217,130,43,0.24)' : 'rgba(244,63,94,0.2)',
                      color: ownReview.scores.passedGate ? '#fcd34d' : '#fb7185',
                      border: '1px solid currentColor',
                      borderRadius: 10,
                      padding: '6px 12px',
                      fontWeight: 800,
                      fontSize: 13,
                    }}
                  >
                    {ownReview.assessmentStatus === 'pending-visual-review' ? 'CHỜ KIỂM TRA HÌNH ẢNH' : `${ownReview.scores.passedGate ? 'ĐẠT CHUẨN VIRAL GATE' : 'CẦN CHỈNH SỬA'} · ${ownReview.scores.compositeScore}/10`}
                  </div>
                </div>

                <div className="youwee-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 14 }}>
                  {[
                    { label: 'Hook 3s Đầu', val: ownReview.scores.hookStrength },
                    { label: 'Rõ Bao Bì', val: ownReview.scores.productClarity },
                    { label: 'Money-Shot', val: ownReview.scores.sensoryPayoff },
                    { label: 'Nhịp 14s', val: ownReview.scores.scriptPacing },
                    { label: 'CTA Tự Nhiên', val: ownReview.scores.ctaUrgency },
                  ].map((s) => (
                    <div
                      key={s.label}
                      style={{
                        background: 'rgba(54,34,22,0.75)',
                        borderRadius: 8,
                        padding: '8px 6px',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: 11, color: '#cbb59f' }}>{s.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#fcd34d', marginTop: 2 }}>{ownReview.assessmentStatus === 'pending-visual-review' ? 'Chưa chấm' : `${s.val}/10`}</div>
                    </div>
                  ))}
                </div>

                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#fff8ef', marginBottom: 8 }}>
                  Checklist Tuân Thủ Quy Chuẩn <code>UGC_food_skills</code>:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                  {ownReview.skillComplianceChecklist.map((c) => (
                    <div
                      key={c.ruleId}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        background: 'rgba(54,34,22,0.6)',
                        borderLeft: c.passed ? '3px solid #d9822b' : '3px solid #f43f5e',
                        fontSize: 12,
                      }}
                    >
                      <div style={{ fontWeight: 700, color: c.passed ? '#fcd34d' : '#fb7185' }}>
                        {c.detail.startsWith('Chưa xác minh') ? '○ CHƯA XÁC MINH' : c.passed ? '✓ ĐẠT' : '✗ CẦN SỬA'} · {c.label} (<code>{c.ruleId}</code>)
                      </div>
                      <div style={{ color: '#e6d5c3', marginTop: 2 }}>{c.detail}</div>
                    </div>
                  ))}
                </div>

                {/* HEAD TO HEAD VS COMPETITOR */}
                <div
                  style={{
                    padding: 12,
                    borderRadius: 10,
                    background: 'rgba(181,68,38,0.16)',
                    border: '1px solid rgba(251,146,60,0.35)',
                    marginBottom: 12,
                    fontSize: 12,
                  }}
                >
                  <div style={{ fontWeight: 700, color: '#fdba74', marginBottom: 4 }}>
                    So Kè Trực Tiếp Với Video Đối Thủ ({ownReview.vsCompetitorComparison.competitorFilename} ·{' '}
                    {ownReview.vsCompetitorComparison.competitorHandle})
                  </div>
                  <div style={{ color: '#f5ebe1', marginBottom: 4 }}>
                    <strong>Lợi thế của kênh mình:</strong> {ownReview.vsCompetitorComparison.ourAdvantage}
                  </div>
                  <div style={{ color: '#fbbf24' }}>
                    <strong>Điểm cần tối ưu thêm:</strong> {ownReview.vsCompetitorComparison.gapToClose}
                  </div>
                </div>

                <div style={{ fontSize: 12, fontWeight: 700, color: '#f5ebe1', marginBottom: 6 }}>
                  Khuyến Nghị Cho Lần Render / Đăng Tiếp Theo:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#e6d5c3', lineHeight: 1.55 }}>
                  {ownReview.improvements.map((imp, idx) => (
                    <li key={idx}>{imp}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div
                style={{
                  background: 'rgba(34,21,15,0.75)',
                  border: '1px dashed rgba(196,154,116,0.38)',
                  borderRadius: 14,
                  padding: 32,
                  textAlign: 'center',
                }}
              >
                <p style={{ color: '#e6d5c3', fontSize: 13, marginBottom: 12 }}>
                  Bấm nút <strong>"⚡ Auto Step 2"</strong> hoặc <strong>"Review Video Đang Chọn"</strong> để chấm điểm video generated mới nhất của kênh mình theo 5 trục Viral Gate và đối chiếu trực tiếp với video đối thủ.
                </p>
                <button
                  onClick={() => void handleReviewOwnVideo(selectedOwnVideo ?? ownVideos[0])}
                  style={{
                    background: '#d9822b',
                    color: '#1e120a',
                    border: 'none',
                    borderRadius: 8,
                    padding: '9px 16px',
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Chạy Review Video Mới Nhất Ngay
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SUB-TAB 3: AUTOMATED >=6 WIN PRODUCT IMAGE CURATION & AI PROVISIONING GALLERY */}
      {subTab === 'win_images' && (
        <div className="youwee-split-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.35fr', gap: 18 }}>
          {/* LEFT: AUTO CURATION ENGINE & TODAY_WIN_PRODUCTS LIST */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div
              style={{
                background: 'rgba(34,21,15,0.92)',
                border: '1px solid rgba(217,130,43,0.42)',
                borderRadius: 14,
                padding: 18,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 800, color: '#fbbf24' }}>
                STEP 3 TỰ ĐỘNG · TODAY_WIN_PRODUCTS ↔ WIN_PRODUCTS ARCHIVE
              </div>
              <h3 style={{ margin: '6px 0 8px', fontSize: 16, color: '#fff8ef' }}>
                Đồng Bộ <code>Today_Win_Products</code> &amp; Lọc &gt;= 6 Ảnh Chuẩn AI Provisioning
              </h3>
              <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 12, color: '#e6d5c3', lineHeight: 1.55 }}>
                <li>
                  <strong>Cơ chế lưu trữ thông minh:</strong> Sản phẩm Win của những ngày trước trong <code>/data/Products/Today_Win_Products</code> sẽ được chuyển sang kho lưu trữ <code>/data/Products/Win_Products</code> trước mỗi đợt đồng bộ mới.
                </li>
                <li>
                  <strong>Đối chiếu trùng lặp tự động:</strong> Lấy danh sách Win mới nhất từ Ông Chú MMO so sánh với <code>Win_Products</code> — nếu trùng sản phẩm đã có ảnh chuẩn thì copy ngay sang <code>Today_Win_Products</code>; nếu là sản phẩm Win mới thì tự động tải &amp; lọc <strong>&gt;= 6 ảnh chuẩn AI</strong>.
                </li>
                <li>
                  <strong>Món nấu DIY (Sốt, Mắm, Đồ Khô, Nguyên Liệu Nấu):</strong> Tự động thu thập đủ ảnh <em>(1) Bao bì chuẩn</em>, <em>(2) Lúc đang nấu/chế biến</em>, và <em>(3) Món ăn hoàn thành sau khi nấu xong</em>.
                </li>
                <li>
                  <strong>Đặt tên chuẩn AI Semantic Naming:</strong> <code>01_...</code> đến <code>06_...</code> giúp AI Planner nhận diện tức thì để tạo scene images chuẩn xác nhất.
                </li>
              </ul>

              <button
                onClick={() => void handleAutoRunPipeline('step3')}
                disabled={autoRunningStep !== null}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #b54426, #d9822b)',
                  color: '#fffaf2',
                  border: 'none',
                  borderRadius: 10,
                  padding: '11px 16px',
                  fontSize: 13,
                  fontWeight: 900,
                  cursor: 'pointer',
                  marginBottom: 14,
                }}
              >
                {autoRunningStep === 'step3'
                  ? 'Đang tự động tải, lọc & đặt tên >= 6 ảnh cho Today_Win_Products...'
                  : `⚡ Chạy Tự Động Step 3 Cho Toàn Bộ ${winInventory.length} Sản Phẩm Trong Today_Win_Products`}
              </button>

              {/* Optional single-product re-curate override */}
              <div
                style={{
                  padding: 12,
                  borderRadius: 10,
                  background: 'rgba(20,12,8,0.75)',
                  border: '1px solid rgba(196,154,116,0.24)',
                }}
              >
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#cbb59f', marginBottom: 8 }}>
                  Hoặc chạy lọc lại &gt;= 6 ảnh cho 1 sản phẩm đang chọn từ Bảng Mapping:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <select
                    value={winProductName}
                    onChange={(e) => {
                      setWinProductName(e.target.value);
                      const invMatch = winInventory.find((x) => x.folderName === e.target.value);
                      if (invMatch) setSelectedWinItem(invMatch);
                      const mapMatch = mappingTable.find(
                        (m) => m.winProductFolder === e.target.value || m.productName === e.target.value,
                      );
                      if (mapMatch?.videoFilename && mapMatch.videoFilename !== '(Chưa có video đối thủ khớp trực tiếp)') {
                        setWinCompetitorFile(mapMatch.videoFilename);
                      }
                    }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12 }}
                  >
                    {winInventory.map((item) => (
                      <option key={item.folderName} value={item.folderName}>
                        {item.folderName} ({item.imageFiles.length} ảnh đã kiểm định · {item.sourceImageCount??0} file nguồn · {item.isDiyCookingProduct ? 'DIY Nấu Ăn' : 'Tiêu chuẩn'})
                      </option>
                    ))}
                  </select>

                  <div className="youwee-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <select
                      value={winSource}
                      onChange={(e) => setWinSource(e.target.value as WinProductImageSource)}
                      style={{ padding: '8px 10px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12 }}
                    >
                      <option value="auto">Tự động gộp 3 nguồn (MMO + Web + Video đối thủ)</option>
                      <option value="mmo">Ưu tiên Ông Chú MMO + Bù đủ &gt;= 6 ảnh</option>
                      <option value="web">Ưu tiên Web Search + Bù đủ &gt;= 6 ảnh</option>
                      <option value="competitor_video">Ưu tiên Video Đối Thủ + Bù đủ &gt;= 6 ảnh</option>
                    </select>
                    <input
                      value={winSearchQuery}
                      onChange={(e) => setWinSearchQuery(e.target.value)}
                      placeholder="Từ khóa bổ sung (tùy chọn)"
                      style={{ padding: '8px 10px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12 }}
                    />
                  </div>

                  <button
                    onClick={() => void handleCurateSingleWinProduct()}
                    disabled={acquiringImages}
                    style={{
                      background: '#d9822b',
                      color: '#1e120a',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 12px',
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {acquiringImages ? 'Đang tải & lọc >= 6 ảnh...' : `Tải & Lọc >= 6 Ảnh Cho "${winProductName}"`}
                  </button>
                </div>
              </div>
            </div>

            {/* TODAY WIN PRODUCTS INVENTORY SELECTOR */}
            <div
              style={{
                background: 'rgba(34,21,15,0.9)',
                border: '1px solid rgba(196,154,116,0.26)',
                borderRadius: 14,
                padding: 16,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 13.5, color: '#fff8ef', marginBottom: 10 }}>
                Kho Sản Phẩm Win Hôm Nay ({winInventory.length} thư mục trong <code>/Today_Win_Products</code>)
              </div>
              <div style={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {winInventory.map((item) => {
                  const active = selectedWinItem?.folderName === item.folderName;
                  return (
                    <div
                      key={item.folderName}
                      onClick={() => {
                        setSelectedWinItem(item);
                        setWinProductName(item.folderName);
                      }}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: active ? 'rgba(217,130,43,0.22)' : 'rgba(49,31,21,0.6)',
                        border: active ? '1px solid #fbbf24' : '1px solid rgba(196,154,116,0.18)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#fff8ef' }}>
                          {item.folderName}
                          {item.isDiyCookingProduct && (
                            <span
                              style={{
                                marginLeft: 6,
                                padding: '1px 6px',
                                borderRadius: 6,
                                background: 'rgba(251,146,60,0.22)',
                                color: '#fb923c',
                                fontSize: 10,
                                fontWeight: 800,
                              }}
                            >
                              DIY Nấu Ăn
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: '#cbb59f', marginTop: 2 }}>
                          {item.matchedCompetitorVideo ? (
                            <>Video đối thủ: <code>{item.matchedCompetitorVideo}</code></>
                          ) : (
                            <>Nguồn: Ông Chú MMO + Kho Win_Products</>
                          )}
                        </div>
                      </div>
                      <span
                        style={{
                          background: item.curationReport?.policyVersion === 5 && item.curationReport.readyForProduction === true ? 'rgba(217,130,43,0.24)' : 'rgba(251,191,36,0.2)',
                          color: item.curationReport?.policyVersion === 5 && item.curationReport.readyForProduction === true ? '#fcd34d' : '#fbbf24',
                          padding: '3px 9px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {item.imageFiles.length} ảnh đã kiểm định · {item.sourceImageCount??0} file nguồn
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT: VISUAL GALLERY OF >=6 CURATED SEMANTIC AI IMAGES + QUALITY FILTER REPORT */}
          <div
            style={{
              background: 'rgba(34,21,15,0.9)',
              border: '1px solid rgba(217,130,43,0.34)',
              borderRadius: 14,
              padding: 18,
            }}
          >
            {selectedWinItem ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#fb923c' }}>
                      ẢNH THAM CHIẾU ({selectedWinItem.imageFiles.length} ẢNH SẴN SÀNG · {selectedWinItem.curationReport?.acceptedSourceImages?.length??0} ẢNH NGUỒN ĐẠT RIÊNG LẺ)
                    </div>
                    <h3 style={{ margin: '4px 0', fontSize: 17, color: '#fff8ef' }}>
                      {selectedWinItem.folderName}
                    </h3>
                    <div style={{ fontSize: 11.5, color: '#cbb59f', wordBreak: 'break-all' }}>
                      Đường dẫn: <code>{selectedWinItem.folderPath}</code>
                    </div>
                  </div>
                  <span
                    style={{
                      padding: '5px 10px',
                      borderRadius: 8,
                      background: selectedWinItem.isDiyCookingProduct ? 'rgba(251,146,60,0.22)' : 'rgba(181,68,38,0.22)',
                      color: selectedWinItem.isDiyCookingProduct ? '#fb923c' : '#fdba74',
                      fontSize: 11.5,
                      fontWeight: 800,
                    }}
                  >
                    {selectedWinItem.isDiyCookingProduct
                      ? '🍲 Món cần chế biến: yêu cầu ảnh bao bì, đang nấu và thành phẩm'
                      : '📦 Yêu cầu ít nhất 6 ảnh nguồn khác biệt và đủ vai trò'}
                  </span>
                </div>

                {lastAcquiredResult && lastAcquiredResult.productName === selectedWinItem.folderName && (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: 10,
                      borderRadius: 10,
                      background: 'rgba(217,130,43,0.18)',
                      border: '1px solid rgba(251,191,36,0.45)',
                      fontSize: 12,
                      color: '#fde68a',
                    }}
                  >
                    {lastAcquiredResult.curationReport?.readyForProduction?'✓':'⚠'} {lastAcquiredResult.details}
                  </div>
                )}

                <div role="status" style={{ marginBottom: 12, color: '#fde68a', fontSize: 13 }}>
                  {selectedWinItem.curationReport?.policyVersion === 5 && selectedWinItem.curationReport.readyForProduction === true
                    ? 'Bộ ảnh đã xác minh đủ vai trò tham chiếu.'
                    : `Bộ ảnh cần bổ sung hoặc kiểm tra lại: ${selectedWinItem.curationReport?.missingRoles?.join(', ') || 'bao bì ngoài, ruột gói và công đoạn phù hợp'}.`}
                </div>
                {selectedWinItem.curationReport?.checkpoints?.length ? (
                  <div className="mb-3 space-y-1 text-xs">
                    {selectedWinItem.curationReport.checkpoints.map(check=>(
                      <div key={check.id} style={{color:check.passed?'#86efac':'#fbbf24'}}>
                        {check.passed?'✓':'✗'} {check.required} · Thực tế: {check.observed||'Chưa đủ bằng chứng'}
                      </div>
                    ))}
                  </div>
                ):null}
                {/* IMAGE CARDS GRID */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                  {(selectedWinItem.curationReport?.policyVersion === 5
                    ? selectedWinItem.curationReport.readyForProduction === true
                      ? selectedWinItem.curationReport.curatedImages.filter(img=>selectedWinItem.imageFiles.includes(img.filename))
                      : selectedWinItem.curationReport.acceptedSourceImages??[]
                    : []
                  ).map((img) => {
                    const badge = STAGE_BADGE_MAP[img.stage] ?? STAGE_BADGE_MAP.packaging_raw!;
                    const imgUrl =
                      img.streamUrl ||
                      `/api/affiliate-harness/win-products/image/${encodeURIComponent(
                        selectedWinItem.folderName,
                      )}/${encodeURIComponent(img.filename)}`;
                    return (
                      <div
                        key={img.filename}
                        style={{
                          background: 'rgba(20,12,8,0.86)',
                          border: '1px solid rgba(196,154,116,0.24)',
                          borderRadius: 12,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <div style={{ position: 'relative', height: 165, background: '#120a06' }}>
                          <img
                            src={imgUrl}
                            alt={img.filename}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              padding: '3px 8px',
                              borderRadius: 6,
                              background: badge.bg,
                              color: badge.color,
                              border: `1px solid ${badge.color}`,
                              fontSize: 10.5,
                              fontWeight: 800,
                              backdropFilter: 'blur(6px)',
                            }}
                          >
                            {badge.label}
                          </span>
                          <span
                            style={{
                              position: 'absolute',
                              bottom: 8,
                              right: 8,
                              padding: '2px 7px',
                              borderRadius: 6,
                              background: 'rgba(28,17,12,0.88)',
                              color: '#fcd34d',
                              fontSize: 10.5,
                              fontWeight: 800,
                            }}
                          >
                            {selectedWinItem.curationReport?.readyForProduction?'Bộ ảnh đạt kiểm định':'Ảnh nguồn đạt riêng lẻ · Bộ chưa đủ'}
                          </span>
                        </div>
                        <div style={{ padding: 10, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <div>
                            <div
                              style={{
                                fontFamily: 'monospace',
                                fontSize: 11,
                                fontWeight: 700,
                                color: '#fdba74',
                                wordBreak: 'break-all',
                              }}
                            >
                              {img.filename}
                            </div>
                            <div style={{ fontSize: 11.5, color: '#e6d5c3', marginTop: 4, lineHeight: 1.4 }}>
                              {img.reviewNoteVi}
                            </div>
                          </div>
                          <div style={{ marginTop: 8, fontSize: 10.5, color: '#cbb59f', display: 'flex', justifyContent: 'space-between' }}>
                            <span>Nguồn: {img.source.toUpperCase()}</span>
                            <span style={{ color: '#fcd34d' }}>✓ Rõ SP · Không che khuất</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* REJECTED CANDIDATES REPORT (QUALITY GATE TRANSPARENCY) */}
                {selectedWinItem.curationReport?.rejectedCandidates &&
                  selectedWinItem.curationReport.rejectedCandidates.length > 0 && (
                    <div
                      style={{
                        marginTop: 16,
                        padding: 12,
                        borderRadius: 10,
                        background: 'rgba(244,63,94,0.1)',
                        border: '1px solid rgba(244,63,94,0.3)',
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#fda4af', marginBottom: 6 }}>
                        Báo Cáo Lọc Bỏ Ảnh Kém Chất Lượng ({selectedWinItem.curationReport.rejectedCandidates.length} ảnh ứng viên đã bị loại):
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: '#fecdd3', lineHeight: 1.5 }}>
                        {selectedWinItem.curationReport.rejectedCandidates.map((rej, idx) => (
                          <li key={idx}>
                            <code>{rej.candidateName}</code> ({rej.source}) — {rej.reasonVi}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            ) : (
              <div style={{ padding: 32, textAlign: 'center', color: '#cbb59f' }}>
                Chọn một sản phẩm Win ở cột bên trái để xem bộ &gt;= 6 ảnh chuẩn AI Provisioning.
              </div>
            )}
          </div>
        </div>
      )}

      {/* SUB-TAB 4: UGC_FOOD_SKILLS IN KBS_RAG_MCP AUDIT & LIVE UPDATER */}
      {subTab === 'food_skills' && skillsAudit && (
        <div className="youwee-split-grid" style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 18 }}>
          <div
            style={{
              background: 'rgba(34,21,15,0.9)',
              border: '1px solid rgba(217,130,43,0.34)',
              borderRadius: 14,
              padding: 18,
            }}
          >
            <div className="youwee-header-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#fb923c' }}>
                  KIỂM ĐỊNH KHO TRI THỨC ẨM THỰC (KBS_RAG_MCP)
                </div>
                <h3 style={{ margin: '4px 0 0', fontSize: 16, color: '#fff8ef' }}>
                  Trạng Thái <code>UGC_food_skills</code> ({skillsAudit.domain})
                </h3>
              </div>
              <div className="youwee-action-bar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => void handleAutoRunPipeline('step4')}
                  disabled={autoRunningStep !== null}
                  style={{
                    background: 'rgba(217,130,43,0.22)',
                    color: '#fcd34d',
                    border: '1px solid rgba(251,191,36,0.48)',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 11.5,
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  {autoRunningStep === 'step4' ? 'Đang chạy Auto Step 4...' : '⚡ Chạy Tự Động Step 4 (Từ Today_Win_Products)'}
                </button>
                <span
                  style={{
                    background: 'rgba(217,130,43,0.22)',
                    color: '#fcd34d',
                    border: '1px solid currentColor',
                    borderRadius: 999,
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {skillsAudit.liveMcpConnected ? '● RAG_KBS_MCP ONLINE (Port 8000)' : '● Snapshot Verified'}
                </span>
              </div>
            </div>

            <div className="youwee-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
              <div style={{ background: 'rgba(54,34,22,0.72)', padding: 10, borderRadius: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#cbb59f' }}>Hồ sơ Món ăn</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#fcd34d' }}>
                  {skillsAudit.profilesWithSamples}/{skillsAudit.totalProfiles} (100%)
                </div>
              </div>
              <div style={{ background: 'rgba(54,34,22,0.72)', padding: 10, borderRadius: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#cbb59f' }}>Knowledge Units</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#fdba74' }}>{skillsAudit.totalUnits}</div>
              </div>
              <div style={{ background: 'rgba(54,34,22,0.72)', padding: 10, borderRadius: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#cbb59f' }}>Knowledge Atoms</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#fff8ef' }}>{skillsAudit.totalAtoms}</div>
              </div>
              <div style={{ background: 'rgba(54,34,22,0.72)', padding: 10, borderRadius: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#cbb59f' }}>Hạn từ vựng GenZ</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fbbf24' }}>{skillsAudit.genzFreshValidUntil}</div>
              </div>
            </div>

            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#fcd34d', marginBottom: 6 }}>
              Các Điểm Đã Rà Soát, Sửa Lỗi &amp; Bổ Sung Vào <code>UGC_food_skills</code>:
            </div>
            <ul style={{ margin: '0 0 14px', paddingLeft: 18, fontSize: 12, color: '#e6d5c3', lineHeight: 1.55 }}>
              {skillsAudit.recentAuditFixes.map((fix, i) => (
                <li key={i}>{fix}</li>
              ))}
            </ul>

            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#fff8ef', marginBottom: 6 }}>
              5 Quy Tắc Cốt Lõi Đang Khóa Trong Pipeline:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {skillsAudit.coreLockedRules.map((rule) => (
                <div
                  key={rule.id}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    background: 'rgba(54,34,22,0.62)',
                    borderLeft: '3px solid #fb923c',
                    fontSize: 12,
                  }}
                >
                  <div style={{ fontWeight: 700, color: '#fff8ef' }}>
                    {rule.title} (<code>{rule.id}</code>)
                  </div>
                  <div style={{ color: '#cbb59f', marginTop: 2 }}>{rule.summary}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: LIVE SKILL UPDATER */}
          <div
            style={{
              background: 'rgba(34,21,15,0.9)',
              border: '1px solid rgba(217,130,43,0.34)',
              borderRadius: 14,
              padding: 18,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#fcd34d' }}>
              CẬP NHẬT THÊM KINH NGHIỆM / MẪU CÂU MỚI VÀO KBS_RAG_MCP
            </div>
            <h3 style={{ margin: '6px 0 8px', fontSize: 16, color: '#fff8ef' }}>
              Bổ Sung Quy Tắc Hoặc Học Tập Từ Đối Thủ Vào <code>UGC_food_skills</code>
            </h3>
            <p style={{ fontSize: 12, color: '#cbb59f', marginTop: 0, marginBottom: 12 }}>
              Khi phát hiện một kiểu quay Money-Shot mới hoặc câu thoại bắt trend từ đối thủ, bạn có thể lưu trực tiếp vào <code>mcp-snapshot.json</code> và <code>competitors.md</code> tại đây (hoặc bấm <strong>⚡ Auto Step 4</strong> để hệ thống tự động đúc kết từ <code>Today_Win_Products</code>).
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: '#e6d5c3', display: 'block', marginBottom: 4 }}>
                  Nhóm món áp dụng (48 Dish Profiles):
                </label>
                <select
                  value={skillDishSlug}
                  onChange={(e) => setSkillDishSlug(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12.5 }}
                >
                  {skillsAudit.dishProfiles.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.name} ({p.slug}) — {p.aliasesCount} từ khóa
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: 12, color: '#e6d5c3', display: 'block', marginBottom: 4 }}>
                  Tiêu đề quy tắc / mẫu học tập mới:
                </label>
                <input
                  value={skillTitle}
                  onChange={(e) => setSkillTitle(e.target.value)}
                  placeholder="VD: Cận cảnh rưới sốt muối ớt xanh chảy tràn trên tôm hấp"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12.5 }}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: '#e6d5c3', display: 'block', marginBottom: 4 }}>
                  Nội dung chi tiết (hành động hình ảnh, góc máy, hoặc câu thoại mẫu):
                </label>
                <textarea
                  rows={5}
                  value={skillContent}
                  onChange={(e) => setSkillContent(e.target.value)}
                  placeholder="Nhập mô tả chi tiết để AI Planner áp dụng cho các video tiếp theo..."
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, background: '#1b110c', border: '1px solid #5c3d28', color: '#fff8ef', fontSize: 12.5 }}
                />
              </div>

              <button
                onClick={() => void handleAppendFoodSkill()}
                disabled={updatingSkill}
                style={{
                  background: 'linear-gradient(135deg, #b54426, #d9822b)',
                  color: '#fffaf2',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                {updatingSkill ? 'Đang ghi vào UGC_food_skills...' : 'Cập Nhật Vào UGC_food_skills & KBS_RAG_MCP'}
              </button>

              {skillUpdateNotice && (
                <div style={{ padding: 10, borderRadius: 8, background: 'rgba(217,130,43,0.2)', color: '#fcd34d', fontSize: 12 }}>
                  {skillUpdateNotice}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SUB-TAB 5: CLASSIC YOUWEE EMBED */}
      {subTab === 'classic_youwee' && (
        <div
          style={{
            height: 'calc(100vh - 240px)',
            minHeight: 460,
            borderRadius: 14,
            overflow: 'hidden',
            border: '1px solid rgba(196,154,116,0.32)',
          }}
        >
          <iframe
            src="/youwee/"
            title="Youwee Downloader"
            style={{ width: '100%', height: '100%', border: 'none', background: '#180f0a' }}
            allow="clipboard-read; clipboard-write"
          />
        </div>
      )}
      </div>
    </div>
  );
};
